import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Clock,
  MapPin,
  Camera,
  CheckCircle,
  X,
  ArrowLeft,
  ArrowRight,
  Menu,
  LogIn,
  LogOut,
  Coffee,
  RotateCcw,
  Smartphone,
  Wifi,
  WifiOff,
  Battery,
  Signal,
  RotateCw,
  Map,
  Navigation,
  Target,
  Lock,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const funcionarioDispensaGeofence = (funcionario) => {
  const tipoContrato = String(funcionario?.tipo_contrato || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[ -]/g, '_');
  return ['home_office', 'homeoffice', 'hibrido', 'hibrida'].includes(tipoContrato);
};

// Funções auxiliares
const formatarHoras = (horasDecimal) => {
  if (horasDecimal === null || horasDecimal === undefined) return '00:00';
  const negativo = horasDecimal < 0;
  const abs = Math.abs(horasDecimal);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${negativo ? '-' : ''}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatarDataHora = (dateString) => {
  return new Date(dateString).toLocaleTimeString('pt-BR');
};

const getTipoMarcacaoBadge = (tipo) => {
  const variants = {
    'ENTRADA': { color: 'bg-green-100 text-green-800', label: 'Entrada' },
    'SAIDA': { color: 'bg-red-100 text-red-800', label: 'Saída' },
    'SAIDA_INTERVALO': { color: 'bg-yellow-100 text-yellow-800', label: 'Saída Intervalo' },
    'RETORNO_INTERVALO': { color: 'bg-blue-100 text-blue-800', label: 'Retorno Intervalo' }
  };
  
  const config = variants[tipo] || { color: 'bg-gray-100 text-gray-800', label: tipo };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

const PontoPWAPage = () => {
  const { user, logout, login } = useAuth();
  const { toast } = useToast();
  
  // Estados principais
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loginRequired, setLoginRequired] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [location, setLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState('success'); // Simplificado para teste
  const [showMapModal, setShowMapModal] = useState(false); // NOVO: Modal do mapa
  const [multipleReadings, setMultipleReadings] = useState([]); // NOVO: Múltiplas leituras GPS
  const [recalibrando, setRecalibrando] = useState(false); // NOVO: Status de recalibração
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoRegistros, setHistoricoRegistros] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [filtroHistorico, setFiltroHistorico] = useState('30dias'); // 'dia', '7dias', '15dias', '30dias', 'periodo'
  const [dataInicioCustom, setDataInicioCustom] = useState('');
  const [dataFimCustom, setDataFimCustom] = useState('');
  const [registrandoPonto, setRegistrandoPonto] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedAcao, setSelectedAcao] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // FRONTAL por padrão para selfie
  const [cameraReady, setCameraReady] = useState(false);
  const [loadingCamera, setLoadingCamera] = useState(false);
  const [editandoAjusteId, setEditandoAjusteId] = useState(null);
  
  // Refs para camera
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Estados para controle de validação de ponto
  const [registrosHoje, setRegistrosHoje] = useState([]);
  const [carregandoRegistros, setCarregandoRegistros] = useState(false);
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState([]);
  
  // Estados para modal de ajuste de ponto esquecido
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [showAjustesSolicitados, setShowAjustesSolicitados] = useState(false);
  const [ajustesSolicitados, setAjustesSolicitados] = useState([]);
  const [carregandoAjustes, setCarregandoAjustes] = useState(false);
  
  // NOVO: Estados para modal unificado de solicitações
  const [tipoSolicitacao, setTipoSolicitacao] = useState('ponto_esquecido'); // ponto_esquecido, correcao, abono_falta, banco_horas
  const [justificativasFalta, setJustificativasFalta] = useState([]);
  const [justificativaId, setJustificativaId] = useState('');
  const [horasAbono, setHorasAbono] = useState('');
  const [dataFimAbono, setDataFimAbono] = useState('');
  
  // Estados específicos para correção de registro
  const [registrosParaCorrigir, setRegistrosParaCorrigir] = useState([]);
  const [registroSelecionado, setRegistroSelecionado] = useState(null);
  const [buscandoRegistros, setBuscandoRegistros] = useState(false);
  
  // Estados para envio de documentos
  const [showEnviarDocumento, setShowEnviarDocumento] = useState(false);
  const [showDocumentosEnviados, setShowDocumentosEnviados] = useState(false);
  const [meusDocumentos, setMeusDocumentos] = useState([]);
  const [carregandoMeusDocumentos, setCarregandoMeusDocumentos] = useState(false);
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  
  // NOVO: Estado para Banco de Horas
  const [saldoBancoHoras, setSaldoBancoHoras] = useState(null);
  const [carregandoSaldo, setCarregandoSaldo] = useState(false);
  
  const [documentoForm, setDocumentoForm] = useState({
    tipo_documento_id: '',
    motivo: '',
    arquivo: null
  });
  const [enviandoDocumento, setEnviandoDocumento] = useState(false);
  const [ajusteData, setAjusteData] = useState('');
  const [ajusteHora, setAjusteHora] = useState('');
  const [ajusteTipo, setAjusteTipo] = useState('entrada');
  const [ajusteJustificativa, setAjusteJustificativa] = useState('');
  const [enviandoAjuste, setEnviandoAjuste] = useState(false);
  
  // Estados para Geofences
  const [geofences, setGeofences] = useState([]);
  const [geofencesDoColaborador, setGeofencesDoColaborador] = useState([]);
  const [carregandoGeofences, setCarregandoGeofences] = useState(false);
  const [dentroDaArea, setDentroDaArea] = useState(null); // null = não verificado, true/false = resultado
  const [geofenceMaisProxima, setGeofenceMaisProxima] = useState(null);
  const [distanciaMinima, setDistanciaMinima] = useState(null);
  
  // Estado para dados do funcionário
  const [dadosFuncionario, setDadosFuncionario] = useState(null);
  
  // Estados para Solicitação de Férias
  const [showSolicitarFerias, setShowSolicitarFerias] = useState(false);
  const [meusPeriodosFerias, setMeusPeriodosFerias] = useState([]);
  const [minhasSolicitacoesFerias, setMinhasSolicitacoesFerias] = useState([]);
  const [showNovaFeriasModal, setShowNovaFeriasModal] = useState(false);
  const [periodoFeriasSelecionado, setPeriodoFeriasSelecionado] = useState(null);
  const [formFerias, setFormFerias] = useState({
    start_date: '',
    end_date: '',
    days_of_enjoyment: 0,
    notes: '',
    sell_vacation_days: false,
    days_to_sell: 0,
    request_13th_advance: false
  });

  // Ordem obrigatória das marcações
  // Labels para mensagens
  const LABEL_MARCACOES = {
    'ENTRADA': 'Entrada',
    'SAIDA_INTERVALO': 'Saída para Intervalo',
    'RETORNO_INTERVALO': 'Retorno do Intervalo',
    'SAIDA': 'Saída'
  };

  // Buscar dados do funcionário
  const buscarDadosFuncionario = async () => {
    if (!user || !user.employee_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/employees/${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDadosFuncionario(response.data);
      
      // NOVO: Buscar saldo de banco de horas
      buscarSaldoBancoHoras();
    } catch (error) {
      console.error('Erro ao buscar dados do funcionário:', error);
    }
  };
  
  // NOVO: Função para buscar saldo de banco de horas
  const buscarSaldoBancoHoras = async () => {
    if (!user || !user.employee_id) return;
    
    try {
      setCarregandoSaldo(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/banco-horas/saldo-diario/${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaldoBancoHoras(response.data);
    } catch (error) {
      console.error('Erro ao buscar saldo de banco de horas:', error);
      setSaldoBancoHoras(null);
    } finally {
      setCarregandoSaldo(false);
    }
  };
  
  // NOVO: Carregar justificativas de falta
  const carregarJustificativas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/justificativas-falta?apenas_ativas=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJustificativasFalta(response.data);
    } catch (error) {
      console.error('Erro ao carregar justificativas:', error);
    }
  };

  // Buscar registros de ponto do dia atual
  const buscarRegistrosHoje = async () => {
    if (!user || !user.employee_id) return;
    
    try {
      setCarregandoRegistros(true);
      const token = localStorage.getItem('token');
      
      // IMPORTANTE: Usar data LOCAL do navegador, não UTC
      // Se o navegador está no Brasil, ele vai retornar a data correta do Brasil
      const agora = new Date();
      const ano = agora.getFullYear();
      const mes = String(agora.getMonth() + 1).padStart(2, '0');
      const dia = String(agora.getDate()).padStart(2, '0');
      const hoje = `${ano}-${mes}-${dia}`;
      
      console.log('🔍 PWA - Buscando registros para:', {
        employee_id: user.employee_id,
        dataLocal: hoje,
        horarioAtual: agora.toLocaleString('pt-BR')
      });
      
      const response = await axios.get(
        `${API}/api/timetracking/registros/${user.employee_id}?data_inicio=${hoje}&data_fim=${hoje}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📦 Resposta do backend:', response.data);
      
      // O backend retorna { funcionario_id, funcionario_nome, registros: [...] }
      const registros = response.data.registros || [];
      setRegistrosHoje(registros);
      console.log('📋 Registros de hoje carregados:', registros.length, 'registro(s)');
    } catch (error) {
      console.error('❌ Erro ao buscar registros de hoje:', error);
      setRegistrosHoje([]);
    } finally {
      setCarregandoRegistros(false);
    }
  };

  // Validar se pode registrar o tipo de ponto
  const validarRegistroPonto = (tipoDesejado) => {
    console.log('🔍 Validando registro de ponto:', tipoDesejado);
    console.log('📋 Registros existentes hoje:', registrosHoje);
    console.log('📋 Total de registros:', registrosHoje.length);
    
    const ordenados = [...registrosHoje].sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));
    const ultimoTipo = (ordenados[ordenados.length - 1]?.tipo_marcacao || ordenados[ordenados.length - 1]?.tipo || '').toUpperCase();
    if (tipoDesejado === 'ENTRADA' && ordenados.length > 0) return { valido: false, mensagem: 'A entrada já foi registrada' };
    if (tipoDesejado === 'RETORNO_INTERVALO' && ultimoTipo !== 'SAIDA_INTERVALO') return { valido: false, mensagem: 'Registre uma saída antes do retorno' };
    if (tipoDesejado === 'SAIDA_INTERVALO' && ultimoTipo === 'SAIDA_INTERVALO') return { valido: false, mensagem: 'Registre o retorno antes de sair novamente' };
    if (tipoDesejado === 'SAIDA' && ultimoTipo === 'SAIDA_INTERVALO') return { valido: false, mensagem: 'Registre o retorno antes de encerrar' };
    if (tipoDesejado === 'SAIDA' && ultimoTipo === 'SAIDA') return { valido: false, mensagem: 'A jornada já foi encerrada' };
    
    // Verificar se horário atual é posterior ao último registro
    if (registrosHoje.length > 0) {
      const ultimoRegistro = registrosHoje[registrosHoje.length - 1];
      const ultimaDataHora = new Date(ultimoRegistro.data_hora);
      const agora = new Date();
      
      console.log(`⏰ Último registro: ${ultimaDataHora.toLocaleString()}`);
      console.log(`⏰ Agora: ${agora.toLocaleString()}`);
      
      if (agora <= ultimaDataHora) {
        return {
          valido: false,
          mensagem: `${LABEL_MARCACOES[tipoDesejado]} não pode ser registrado antes ou igual ao último registro`
        };
      }
    }
    
    // Tudo OK
    console.log(`✅ Validação OK para ${tipoDesejado}`);
    return {
      valido: true,
      mensagem: 'Registro válido'
    };
  };

  // Obter próximo tipo de ponto permitido
  // Buscar solicitações de ajuste pendentes do funcionário
  const buscarSolicitacoesPendentes = async () => {
    if (!user || !user.employee_id) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar pontos esquecidos
      const responsePontos = await axios.get(
        `${API}/api/timetracking/solicitacoes-ponto-esquecido?funcionario_id=${user.employee_id}&status=pendente`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // NOVO: Buscar ajustes/correções pendentes
      const responseAjustes = await axios.get(
        `${API}/api/timetracking/ajustes/funcionario/${user.employee_id}?status=PENDENTE`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Combinar e filtrar apenas as solicitações de hoje
      const hoje = new Date().toISOString().split('T')[0];
      const pontos = responsePontos.data.filter(sol => sol.data === hoje);
      const ajustes = (responseAjustes.data || []).filter(sol => {
        // Ajustes usam data_hora_nova, extrair apenas a data
        const data = sol.data_hora_nova?.split('T')[0] || '';
        return data === hoje;
      });
      
      const totalSolicitacoes = [...pontos, ...ajustes];
      
      console.log('📝 Solicitações pendentes de hoje:', {
        pontos: pontos.length,
        ajustes: ajustes.length,
        total: totalSolicitacoes.length
      });
      
      setSolicitacoesPendentes(totalSolicitacoes);
    } catch (error) {
      console.error('Erro ao buscar solicitações pendentes:', error);
    }
  };

  const carregarTiposDocumentos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/tipos-documentos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTiposDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar tipos de documentos",
        variant: "destructive"
      });
    }
  };

  const handleEnviarDocumento = async () => {
    if (!documentoForm.tipo_documento_id || !documentoForm.motivo || !documentoForm.arquivo) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos e selecione um arquivo",
        variant: "destructive"
      });
      return;
    }

    setEnviandoDocumento(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', documentoForm.arquivo);
      formData.append('tipo_documento_id', documentoForm.tipo_documento_id);
      formData.append('motivo', documentoForm.motivo);

      await axios.post(
        `${API}/api/documentos/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: "Documento enviado!",
        description: "Seu documento foi enviado com sucesso",
      });

      // Reset form
      setDocumentoForm({
        tipo_documento_id: '',
        motivo: '',
        arquivo: null
      });
      setShowEnviarDocumento(false);
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível enviar o documento",
        variant: "destructive"
      });
    } finally {
      setEnviandoDocumento(false);
    }
  };

  const carregarMeusDocumentos = async () => {
    setCarregandoMeusDocumentos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/documentos/meus`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMeusDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar seus documentos",
        variant: "destructive"
      });
    } finally {
      setCarregandoMeusDocumentos(false);
    }
  };

  // Funções de Férias
  const carregarDadosFerias = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Carregar períodos
      const periodosRes = await axios.get(`${API}/api/vacations/my-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeusPeriodosFerias(periodosRes.data.periods || []);
      
      // Carregar solicitações
      const solicitacoesRes = await axios.get(`${API}/api/vacations/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinhasSolicitacoesFerias(solicitacoesRes.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados de férias:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar dados de férias",
        variant: "destructive"
      });
    }
  };

  const calcularDiasFerias = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return 0;
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    if (fim < inicio) return 0;
    return Math.floor((fim - inicio) / (1000 * 60 * 60 * 24)) + 1;
  };

  const enviarSolicitacaoFerias = async () => {
    if (!formFerias.start_date || !formFerias.end_date) {
      toast({
        title: "Erro",
        description: "Preencha as datas de início e fim",
        variant: "destructive"
      });
      return;
    }

    // Validar se os dias solicitados não excedem os disponíveis
    if (formFerias.days_of_enjoyment > periodoFeriasSelecionado.days_remaining) {
      toast({
        title: "Erro",
        description: `Você tem apenas ${periodoFeriasSelecionado.days_remaining} dias disponíveis, mas solicitou ${formFerias.days_of_enjoyment} dias.`,
        variant: "destructive"
      });
      return;
    }

    // Validar se a solicitação tem pelo menos 1 dia
    if (formFerias.days_of_enjoyment < 1) {
      toast({
        title: "Erro",
        description: "A solicitação deve ter pelo menos 1 dia.",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/api/vacations/requests`,
        {
          employee_id: user.employee_id,
          vacation_period_id: periodoFeriasSelecionado.id,
          ...formFerias
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Sucesso!",
        description: "Solicitação enviada! Aguarde aprovação do RH.",
      });
      
      setShowNovaFeriasModal(false);
      setFormFerias({
        start_date: '',
        end_date: '',
        days_of_enjoyment: 0,
        notes: '',
        sell_vacation_days: false,
        days_to_sell: 0,
        request_13th_advance: false
      });
      
      carregarDadosFerias();
      
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao enviar solicitação",
        variant: "destructive"
      });
    }
  };

  const handleEditarAjuste = (ajuste) => {
    // Preencher o formulário com os dados atuais da solicitação
    setEditandoAjusteId(ajuste.id);
    setTipoSolicitacao('ponto_esquecido');
    setAjusteData(ajuste.data);
    setAjusteHora(ajuste.hora);
    setAjusteTipo(ajuste.tipo_ponto);
    setAjusteJustificativa(ajuste.justificativa);
    // Fechar a listagem e abrir o formulário já preenchido
    setShowAjustesSolicitados(false);
    setShowAjusteModal(true);
  };

  const carregarAjustesSolicitados = async () => {
    if (!user || !user.employee_id) return;
    
    setCarregandoAjustes(true);
    
    setCarregandoAjustes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/solicitacoes-ponto-esquecido?funcionario_id=${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Ajustes solicitados:', response.data);
      // Ordenar por data de criação (mais recente primeiro)
      const ajustesOrdenados = response.data.sort((a, b) => 
        new Date(b.criado_em) - new Date(a.criado_em)
      );
      setAjustesSolicitados(ajustesOrdenados);
    } catch (error) {
      console.error('Erro ao carregar ajustes solicitados:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os ajustes solicitados",
        variant: "destructive"
      });
    } finally {
      setCarregandoAjustes(false);
    }
  };

  const buscarHistoricoCompleto = async (filtro = filtroHistorico) => {
    if (!user || !user.employee_id) return;
    
    setCarregandoHistorico(true);
    try {
      const token = localStorage.getItem('token');
      
      let dataInicio, dataFim;
      const hoje = new Date();
      
      // Calcular datas baseado no filtro
      switch (filtro) {
        case 'dia':
          dataInicio = new Date(hoje);
          dataFim = new Date(hoje);
          break;
        case '7dias':
          dataFim = new Date(hoje);
          dataInicio = new Date(hoje);
          dataInicio.setDate(dataInicio.getDate() - 7);
          break;
        case '15dias':
          dataFim = new Date(hoje);
          dataInicio = new Date(hoje);
          dataInicio.setDate(dataInicio.getDate() - 15);
          break;
        case '30dias':
          dataFim = new Date(hoje);
          dataInicio = new Date(hoje);
          dataInicio.setDate(dataInicio.getDate() - 30);
          break;
        case 'periodo':
          if (!dataInicioCustom || !dataFimCustom) {
            toast({
              title: "Datas obrigatórias",
              description: "Selecione a data de início e fim",
              variant: "destructive"
            });
            setCarregandoHistorico(false);
            return;
          }
          dataInicio = new Date(dataInicioCustom);
          dataFim = new Date(dataFimCustom);
          break;
        default:
          dataFim = new Date(hoje);
          dataInicio = new Date(hoje);
          dataInicio.setDate(dataInicio.getDate() - 30);
      }
      
      const response = await axios.get(
        `${API}/api/timetracking/registros/${user.employee_id}?data_inicio=${dataInicio.toISOString().split('T')[0]}&data_fim=${dataFim.toISOString().split('T')[0]}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Histórico completo:', response.data);
      setHistoricoRegistros(response.data.registros || []);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o histórico",
        variant: "destructive"
      });
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const obterAcoesPermitidas = () => {
    console.log('🔍 Obtendo próximo tipo permitido...');
    console.log('📋 Registros hoje:', registrosHoje);
    console.log('📝 Solicitações pendentes:', solicitacoesPendentes);
    
    const ordenados = [...registrosHoje].sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));
    const ultimoTipo = (ordenados[ordenados.length - 1]?.tipo_marcacao || ordenados[ordenados.length - 1]?.tipo || '').toUpperCase();
    if (!ultimoTipo) return [{ tipo: 'ENTRADA', acao: 'ENTRADA', label: 'Entrada' }];
    if (ultimoTipo === 'SAIDA') return [];
    if (ultimoTipo === 'SAIDA_INTERVALO') return [{ tipo: 'RETORNO_INTERVALO', acao: 'RETORNO', label: 'Retorno' }];
    return [
      { tipo: 'SAIDA_INTERVALO', acao: 'SAIDA_TEMPORARIA', label: 'Nova saída temporária' },
      { tipo: 'SAIDA', acao: 'ENCERRAR_JORNADA', label: 'Encerrar jornada' },
    ];
  };

  // Calcular distância entre dois pontos GPS (Fórmula de Haversine)
  const calcularDistancia = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Raio da Terra em metros
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distancia = R * c; // Distância em metros
    return distancia;
  };

  // Buscar geofences e validar localização
  const buscarGeofencesEValidar = async () => {
    if (!user || !user.employee_id) {
      console.log('⚠️ Usuário não autenticado, pulando busca de geofences');
      return;
    }

    try {
      setCarregandoGeofences(true);
      console.log('🔍 Buscando geofences...');
      
      const token = localStorage.getItem('token');
      
      // 1. Buscar dados completos do employee
      const employeeResponse = await axios.get(
        `${API}/api/employees-detailed/${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const employee = employeeResponse.data;
      console.log('👤 Dados do colaborador:', employee);
      console.log('📍 Geofence IDs do colaborador:', employee.geofence_ids);

      if (funcionarioDispensaGeofence(employee)) {
        console.log('✅ Geofence dispensada pela modalidade de trabalho:', employee.tipo_contrato);
        setGeofencesDoColaborador([]);
        setDentroDaArea(true);
        setGeofenceMaisProxima(null);
        setDistanciaMinima(null);
        return;
      }
      
      // 2. Buscar todas as geofences do sistema
      const geofencesResponse = await axios.get(
        `${API}/api/timetracking/geofences`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const todasGeofences = geofencesResponse.data || [];
      console.log('📍 Geofences disponíveis no sistema:', todasGeofences);
      
      setGeofences(todasGeofences);
      
      // 3. Filtrar apenas as geofences do colaborador
      const geofencesIds = employee.geofence_ids || [];
      const geofencesPermitidas = todasGeofences.filter(g => 
        geofencesIds.includes(g.id) && g.ativo
      );
      
      console.log('✅ Geofences permitidas para o colaborador:', geofencesPermitidas);
      setGeofencesDoColaborador(geofencesPermitidas);
      
      // 4. Validar localização atual
      validarLocalizacao(geofencesPermitidas);
      
    } catch (error) {
      console.error('❌ Erro ao buscar geofences:', error);
      setGeofences([]);
      setGeofencesDoColaborador([]);
    } finally {
      setCarregandoGeofences(false);
    }
  };

  // Validar se está dentro de alguma geofence permitida
  const validarLocalizacao = (geofencesPermitidas) => {
    if (funcionarioDispensaGeofence(dadosFuncionario)) {
      setDentroDaArea(true);
      return;
    }

    if (!location || !location.latitude || !location.longitude) {
      console.log('⚠️ Localização não disponível ainda');
      setDentroDaArea(null);
      return;
    }

    if (!geofencesPermitidas || geofencesPermitidas.length === 0) {
      console.log('⚠️ Nenhuma geofence associada ao colaborador');
      setDentroDaArea(false);
      setGeofenceMaisProxima(null);
      setDistanciaMinima(null);
      return;
    }

    console.log('🔍 Validando localização atual:', location);
    
    let dentroDeAlguma = false;
    let menorDistancia = Infinity;
    let geofenceMaisProx = null;
    
    geofencesPermitidas.forEach((geofence) => {
      const distancia = calcularDistancia(
        location.latitude,
        location.longitude,
        geofence.latitude,
        geofence.longitude
      );
      
      console.log(`📏 Distância até ${geofence.nome}: ${distancia.toFixed(2)}m (raio: ${geofence.raio_metros}m)`);
      
      // Verificar se está dentro desta geofence
      if (distancia <= geofence.raio_metros) {
        dentroDeAlguma = true;
        console.log(`✅ DENTRO da geofence: ${geofence.nome}`);
      }
      
      // Atualizar geofence mais próxima
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        geofenceMaisProx = geofence;
      }
    });
    
    setDentroDaArea(dentroDeAlguma);
    setGeofenceMaisProxima(geofenceMaisProx);
    setDistanciaMinima(menorDistancia);
    
    if (dentroDeAlguma) {
      console.log('✅ Colaborador está dentro de área permitida');
    } else {
      console.log('❌ Colaborador está FORA de área permitida');
      console.log(`📍 Geofence mais próxima: ${geofenceMaisProx?.nome} (${menorDistancia.toFixed(2)}m de distância)`);
    }
  };

  // VERIFICAÇÃO CRÍTICA DE AUTENTICAÇÃO - PWA (Estável)
  // Calcular dias de férias automaticamente
  useEffect(() => {
    if (formFerias.start_date && formFerias.end_date) {
      const dias = calcularDiasFerias(formFerias.start_date, formFerias.end_date);
      setFormFerias(prev => ({...prev, days_of_enjoyment: dias}));
    }
  }, [formFerias.start_date, formFerias.end_date]);

  useEffect(() => {
    // Delay para evitar loops de renderização
    const checkAuth = setTimeout(() => {
      // Verificar se usuário está logado
      if (!user) {
        console.log('PWA: Usuário não logado, mostrando tela de login');
        if (!loginRequired) {
          setLoginRequired(true);
        }
        return;
      }
      
      // Verificar se usuário tem employee_id
      if (!user.employee_id) {
        console.log('PWA: Usuário sem employee_id:', user);
        if (loginRequired) {
          setLoginRequired(false);
        }
        return;
      }
      
      // Usuário válido - desabilitar tela de login
      if (loginRequired) {
        console.log('PWA: Usuário autenticado, liberando acesso');
        setLoginRequired(false);
      }
      
      // Buscar dados do funcionário
      buscarDadosFuncionario();
      
      console.log('PWA: Usuário autenticado corretamente:', user?.name, 'employee_id:', user?.employee_id);
    }, 100); // Delay de 100ms para estabilizar
    
    return () => clearTimeout(checkAuth);
  }, [user, loginRequired]); // Incluir loginRequired para evitar loops

  // Atualizar hora a cada segundo (APENAS se usuário autenticado)
  useEffect(() => {
    if (!user || !user.employee_id) {
      return; // Não iniciar timer se não autenticado
    }
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [user]); // Dependência: só inicia timer quando user muda

  // Monitor de conectividade
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 🔄 FUNÇÃO MELHORADA: Buscar localização com múltiplas leituras para maior precisão
  const buscarLocalizacaoAtual = (numLeituras = 1) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não suportada pelo navegador'));
        return;
      }

      console.log(`🌍 Buscando ${numLeituras} leitura(s) GPS...`);
      setGeoStatus('requesting');
      
      const leituras = [];
      let leiturasCompletas = 0;

      const fazerLeitura = (index) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const leitura = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString(),
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed
            };
            
            leituras.push(leitura);
            leiturasCompletas++;
            
            console.log(`✅ Leitura ${leiturasCompletas}/${numLeituras}:`, leitura);
            
            if (leiturasCompletas === numLeituras) {
              // Calcular média das leituras para melhor precisão
              const melhorLocalizacao = numLeituras > 1 
                ? calcularMediaLeituras(leituras)
                : leituras[0];
              
              console.log('📍 Localização final:', melhorLocalizacao);
              setLocation(melhorLocalizacao);
              setMultipleReadings(leituras);
              setGeoStatus('success');
              resolve(melhorLocalizacao);
            } else {
              // Fazer próxima leitura após pequeno delay
              setTimeout(() => fazerLeitura(index + 1), 500);
            }
          },
          (error) => {
            // Tratar erros de geolocalização de forma amigável
            let errorMessage = 'Erro ao obter localização';
            switch (error.code) {
              case 1: // PERMISSION_DENIED
                errorMessage = 'Permissão de localização negada';
                break;
              case 2: // POSITION_UNAVAILABLE
                errorMessage = 'Localização indisponível';
                break;
              case 3: // TIMEOUT
                errorMessage = 'Tempo esgotado ao obter localização';
                break;
              default:
                errorMessage = 'Erro desconhecido ao obter localização';
            }
            console.warn(`⚠️ Erro na leitura ${index + 1}: ${errorMessage}`);
            
            if (leiturasCompletas === 0) {
              setGeoStatus('error');
              reject(new Error(errorMessage));
            } else {
              // Se já tem algumas leituras, usar o que tem
              const melhorLocalizacao = calcularMediaLeituras(leituras);
              setLocation(melhorLocalizacao);
              setGeoStatus('warning');
              resolve(melhorLocalizacao);
            }
          },
          { 
            enableHighAccuracy: true, 
            timeout: 15000, 
            maximumAge: 0  // Sem cache
          }
        );
      };

      fazerLeitura(0);
    });
  };

  // Função auxiliar para calcular média de múltiplas leituras GPS
  const calcularMediaLeituras = (leituras) => {
    if (leituras.length === 0) return null;
    if (leituras.length === 1) return leituras[0];

    // Filtrar leituras com accuracy muito ruim (> 100m)
    const leiturasValidas = leituras.filter(l => l.accuracy < 100);
    const leiturasParaMedia = leiturasValidas.length > 0 ? leiturasValidas : leituras;

    const lat = leiturasParaMedia.reduce((sum, l) => sum + l.latitude, 0) / leiturasParaMedia.length;
    const lng = leiturasParaMedia.reduce((sum, l) => sum + l.longitude, 0) / leiturasParaMedia.length;
    const acc = Math.min(...leiturasParaMedia.map(l => l.accuracy)); // Melhor accuracy

    return {
      latitude: lat,
      longitude: lng,
      accuracy: acc,
      timestamp: new Date().toISOString(),
      fromMultipleReadings: true,
      numReadings: leiturasParaMedia.length
    };
  };

  // 🔄 FUNÇÃO NOVA: Recalibrar GPS (3 leituras para maior precisão)
  const recalibrarGPS = async () => {
    try {
      setRecalibrando(true);
      toast({
        title: "🎯 Recalibrando GPS...",
        description: "Fazendo 3 leituras para maior precisão",
      });

      await buscarLocalizacaoAtual(3); // 3 leituras

      toast({
        title: "✅ GPS Recalibrado!",
        description: `Precisão: ${location?.accuracy?.toFixed(0)}m`,
      });
    } catch (error) {
      toast({
        title: "Erro ao recalibrar",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setRecalibrando(false);
    }
  };

  // Geolocalização (APENAS se usuário autenticado) - mantém comportamento original
  useEffect(() => {
    if (!user || !user.employee_id) {
      return; // Não buscar localização se não autenticado
    }
    
    if (navigator.geolocation) {
      setGeoStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGeoStatus('success');
        },
        (error) => {
          // Tratar erros de geolocalização de forma amigável
          let errorMessage = 'Erro ao obter localização';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permissão de localização negada. Por favor, permita o acesso à sua localização.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Localização indisponível. Verifique se o GPS está ativado.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tempo esgotado ao obter localização. Tente novamente.';
              break;
            default:
              errorMessage = 'Erro desconhecido ao obter localização.';
          }
          console.warn('⚠️ Geolocalização:', errorMessage);
          setGeoStatus('error');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // Alterado para maximumAge: 0
      );
    } else {
      console.warn('⚠️ Geolocalização não suportada pelo navegador');
      setGeoStatus('error');
    }
  }, [user]); // Dependência: só busca localização quando user muda
  
  // Buscar registros de ponto do dia ao autenticar
  useEffect(() => {
    if (user && user.employee_id) {
      buscarRegistrosHoje();
      buscarSolicitacoesPendentes();
      buscarGeofencesEValidar();
      
      // Atualizar registros e solicitações a cada 30 segundos
      const intervalo = setInterval(() => {
        buscarRegistrosHoje();
        buscarSolicitacoesPendentes();
      }, 30000);
      
      return () => clearInterval(intervalo);
    }
  }, [user]);

  // Revalidar localização quando GPS for atualizado
  useEffect(() => {
    if (location && geofencesDoColaborador.length > 0) {
      validarLocalizacao(geofencesDoColaborador);
    }
  }, [location]);

  // Abrir câmera automaticamente quando um tipo de marcação é selecionado
  useEffect(() => {
    if (selectedTipo && !showCamera && !capturedPhoto) {
      console.log('📸 Tipo de marcação selecionado, abrindo câmera automaticamente...');
      // Garantir que começa com câmera frontal
      setFacingMode('user');
      setShowCamera(true);
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        startCamera();
      }, 300);
    }
  }, [selectedTipo]);

  // Calcular automaticamente os dias de gozo quando as datas mudam
  useEffect(() => {
    if (formFerias.start_date && formFerias.end_date) {
      const diasCalculados = calcularDiasFerias(formFerias.start_date, formFerias.end_date);
      setFormFerias(prev => ({
        ...prev,
        days_of_enjoyment: diasCalculados
      }));
    }
  }, [formFerias.start_date, formFerias.end_date]);

  // Iniciar camera
  const startCamera = async (forceRetry = false) => {
    try {
      console.log('🎥 Iniciando câmera...', forceRetry ? '(Retry)' : '');
      setCameraReady(false);
      setLoadingCamera(true);
      
      // Parar câmera anterior se houver
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast({ title: "Erro", description: "Câmera não disponível neste dispositivo", variant: "destructive" });
        console.error('❌ getUserMedia não disponível');
        setLoadingCamera(false);
        return;
      }

      console.log('📱 Dispositivo:', {
        userAgent: navigator.userAgent,
        platform: navigator.platform
      });

      // Usar facingMode do estado (por padrão 'user' para selfie)
      const constraints = {
        video: {
          facingMode: facingMode, // Usa o estado atual (user ou environment)
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      console.log('🔧 Solicitando câmera FRONTAL para selfie:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!stream || !stream.active) {
        throw new Error('Stream não foi obtido ou não está ativo');
      }

      console.log('📺 Stream obtido:', {
        active: stream.active,
        tracks: stream.getVideoTracks().length
      });

      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Configurar atributos essenciais para mobile
        const video = videoRef.current;
        video.playsInline = true;
        video.autoplay = true;
        video.muted = true;
        video.controls = false;
        
        video.setAttribute('playsinline', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('webkit-playsinline', ''); // iOS
        
        video.style.width = '100%';
        video.style.height = 'auto';
        video.style.objectFit = 'cover';
        
        console.log('▶️ Reproduzindo vídeo...');
        
        // Tentar reproduzir
        try {
          await video.play();
          console.log('✅ Vídeo reproduzindo');
        } catch (playError) {
          console.warn('⚠️ Erro ao reproduzir, mas continuando:', playError);
        }
        
        // Aguardar stream estar realmente pronto
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (streamRef.current && streamRef.current.active) {
          console.log('✅ Câmera pronta!');
          setCameraReady(true);
          setLoadingCamera(false);
          toast({ title: "Sucesso!", description: "📸 Câmera pronta para selfie!", variant: "default" });
        }
      }
    } catch (error) {
      console.error('❌ Erro ao iniciar câmera:', error);
      setLoadingCamera(false);
      setCameraReady(false);
      
      // Mensagens de erro mais amigáveis e específicas
      let mensagemErro = 'Erro ao acessar câmera';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        mensagemErro = '🚫 Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do seu navegador.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        mensagemErro = '📱 Nenhuma câmera encontrada neste dispositivo. Verifique se seu dispositivo possui câmera frontal.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        mensagemErro = '⚠️ Câmera está sendo usada por outro aplicativo. Feche outros apps que possam estar usando a câmera.';
      } else if (error.name === 'OverconstrainedError') {
        mensagemErro = '🔧 A câmera frontal não está disponível. Tentando câmera traseira...';
        // Tentar com câmera traseira como fallback
        if (!forceRetry) {
          setFacingMode('environment');
          setTimeout(() => startCamera(true), 1000);
          return;
        }
      } else if (error.name === 'TypeError') {
        mensagemErro = '❌ Seu navegador não suporta acesso à câmera. Use um navegador mais recente como Chrome, Firefox ou Safari.';
      }
      
      toast({ title: "Erro", description: mensagemErro, variant: "destructive" });
      
      // Não tentar novamente automaticamente para evitar loops
      // O usuário pode tentar manualmente
    }
  };

  // Forçar ativação da câmera manualmente
  const forceActivateCamera = () => {
    console.log('🔨 Forçando ativação da câmera...');
    
    if (streamRef.current && streamRef.current.active) {
      setCameraReady(true);
      toast({ title: "Sucesso!", description: "Câmera ativada manualmente!", variant: "default" });
      
      // Forçar configuração do vídeo novamente
      if (videoRef.current && streamRef.current) {
        console.log('🔧 Reconfiguração forçada do vídeo...');
        
        // Limpar e resetar o vídeo
        videoRef.current.srcObject = null;
        videoRef.current.load();
        
        // Aguardar um pouco e reconectar
        setTimeout(() => {
          if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play().catch(err => {
              console.warn('Play forçado falhou:', err);
            });
          }
        }, 100);
      }
    } else {
      toast({ title: "Erro", description: "Stream da câmera não está ativo. Tente reiniciar.", variant: "destructive" });
    }
  };

  // Resetar vídeo manualmente
  const resetVideo = () => {
    console.log('🔄 Resetando vídeo manualmente...');
    
    if (videoRef.current && streamRef.current) {
      try {
        // Parar o vídeo atual
        videoRef.current.srcObject = null;
        
        // Aguardar um pouco e reconectar
        setTimeout(() => {
          if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
            
            // Configurar atributos novamente
            videoRef.current.playsInline = true;
            videoRef.current.autoplay = true;
            videoRef.current.muted = true;
            
            // Tentar reproduzir
            videoRef.current.play().then(() => {
              console.log('✅ Vídeo resetado com sucesso');
              toast({ title: "Sucesso!", description: "Vídeo resetado! Imagem deve aparecer", variant: "default" });
            }).catch(err => {
              console.warn('❌ Erro no play após reset:', err);
              toast({ title: "Aviso", description: "Reset concluído, mas pode precisar tocar na tela", variant: "default" });
            });
          }
        }, 200);
        
      } catch (error) {
        console.error('❌ Erro ao resetar vídeo:', error);
        toast({ title: "Erro", description: "Erro no reset. Tente fechar e abrir novamente.", variant: "destructive" });
      }
    } else {
      toast({ title: "Erro", description: "Vídeo ou stream não disponível", variant: "destructive" });
    }
  };

  // Tentar play manual do vídeo
  const tryManualPlay = async () => {
    if (videoRef.current) {
      try {
        console.log('🔄 Tentando play manual...');
        await videoRef.current.play();
        console.log('✅ Play manual funcionou!');
        setCameraReady(true);
      } catch (error) {
        console.error('❌ Play manual falhou:', error);
        toast({ title: "Erro", description: "Toque no vídeo para ativar", variant: "destructive" });
      }
    }
  };

  // Trocar câmera (frontal/traseira)
  const switchCamera = async () => {
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
    
    // Se a câmera já estiver ativa, reiniciar com a nova configuração
    if (showCamera) {
      stopCamera();
      // Aguardar um pouco antes de reiniciar
      setTimeout(() => {
        startCamera();
      }, 500);
    }
  };

  // Parar camera
  const stopCamera = () => {
    console.log('🛑 Parando câmera...');
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        console.log('🔧 Parando track:', track.kind);
        track.stop();
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setShowCamera(false);
    setCameraReady(false);
    console.log('✅ Câmera parada');
  };

  // Capturar foto
  const capturePhoto = () => {
    console.log('📸 Tentando capturar foto...');
    
    if (!videoRef.current || !canvasRef.current) {
      toast({ title: "Erro", description: "Câmera não está disponível", variant: "destructive" });
      return;
    }
    
    if (!streamRef.current || !streamRef.current.active) {
      toast({ title: "Erro", description: "Stream da câmera não está ativo", variant: "destructive" });
      return;
    }
    
    const video = videoRef.current;
    console.log('📊 Status do vídeo:', {
      readyState: video.readyState,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      currentTime: video.currentTime
    });
    
    try {
      const canvas = canvasRef.current;
      
      // Definir dimensões - usar valores padrão se vídeo não tiver dimensões
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      
      canvas.width = width;
      canvas.height = height;
      
      console.log('🖼️ Capturando com dimensões:', width, 'x', height);
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);
      
      // Converter para base64
      const photoData = canvas.toDataURL('image/jpeg', 0.9);
      
      console.log('📄 Foto gerada, tamanho:', photoData.length);
      
      if (photoData && photoData.length > 1000) {
        setCapturedPhoto(photoData);
        stopCamera();
        toast({ title: "Sucesso!", description: "Foto capturada com sucesso!", variant: "default" });
        
        // Vibração de feedback
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }
        
        console.log('✅ Foto capturada com sucesso, tamanho final:', photoData.length);
      } else {
        console.error('❌ Foto inválida ou muito pequena:', photoData.length);
        toast({ title: "Erro", description: "Foto inválida. Tente novamente ou use foto de teste.", variant: "destructive" });
      }
      
    } catch (error) {
      console.error('❌ Erro ao capturar foto:', error);
      toast({ title: "Erro", description: "Erro ao processar foto. Use foto de teste como alternativa.", variant: "destructive" });
    }
  };

  // Registrar ponto com foto
  const registrarPontoComFoto = async () => {
    console.log('Iniciando registro de ponto...');
    console.log('Foto capturada?', !!capturedPhoto);
    console.log('Usuário?', !!user);
    console.log('Tipo selecionado:', selectedTipo);

    if (!capturedPhoto) {
      toast({ title: "Erro", description: "É obrigatório tirar uma foto para comprovar sua localização", variant: "destructive" });
      return;
    }

    if (!user || !user.employee_id) {
      toast({ title: "Erro", description: "Funcionário não vinculado. Entre em contato com o administrador.", variant: "destructive" });
      return;
    }

    if (!selectedTipo) {
      toast({ title: "Erro", description: "Selecione o tipo de registro", variant: "destructive" });
      return;
    }

    setRegistrandoPonto(true);

    try {
      // 🔒 CRÍTICO: Buscar localização ATUAL do dispositivo (não usar cache)
      console.log('🌍 Buscando localização ATUAL antes do registro...');
      toast({ 
        title: "Obtendo localização...", 
        description: "Aguarde enquanto buscamos sua localização atual",
        duration: 3000
      });

      let localizacaoAtual;
      try {
        localizacaoAtual = await buscarLocalizacaoAtual();
      } catch (error) {
        console.error('❌ Erro ao obter localização:', error);
        toast({ 
          title: "Localização Necessária", 
          description: "Não foi possível obter sua localização. Por favor, habilite a localização no seu dispositivo e permita o acesso.", 
          variant: "destructive",
          duration: 6000
        });
        setRegistrandoPonto(false);
        return;
      }

      // Verificar se a localização foi obtida com sucesso
      if (!localizacaoAtual || !localizacaoAtual.latitude || !localizacaoAtual.longitude) {
        toast({ 
          title: "Localização Necessária", 
          description: "É necessário compartilhar sua localização para registrar o ponto. Por favor, habilite a localização no seu dispositivo e permita o acesso.", 
          variant: "destructive",
          duration: 6000
        });
        setRegistrandoPonto(false);
        return;
      }

      console.log('✅ Usando localização ATUAL para registro:', localizacaoAtual);

      const token = localStorage.getItem('token');
      if (!token) {
        toast({ title: "Erro", description: "Token de autenticação não encontrado", variant: "destructive" });
        setRegistrandoPonto(false);
        return;
      }

      const dados = {
        funcionario_id: user.employee_id,
        tipo_marcacao: selectedTipo,
        acao_marcacao: selectedAcao,
        origem: 'APP',
        latitude: localizacaoAtual.latitude, // Usando localização ATUAL
        longitude: localizacaoAtual.longitude, // Usando localização ATUAL
        foto_comprovante: capturedPhoto,
        dispositivo_info: {
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          is_pwa: true,
          fonte: 'PWA_MOBILE',
          gps_accuracy: localizacaoAtual.accuracy, // Adiciona precisão do GPS
          location_timestamp: localizacaoAtual.timestamp // Adiciona timestamp da localização
        }
      };

      console.log('Enviando dados:', {
        funcionario_id: dados.funcionario_id,
        tipo_marcacao: dados.tipo_marcacao,
        origem: dados.origem,
        tem_foto: !!dados.foto_comprovante,
        foto_size: dados.foto_comprovante?.length,
        user_id: user.id, // Para debug
        employee_id: user.employee_id // Para debug
      });

      const API = process.env.REACT_APP_BACKEND_URL;
      console.log('Endpoint:', `${API}/api/timetracking/registrar`);

      const response = await axios.post(`${API}/api/timetracking/registrar`, dados, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Resposta do servidor:', response.data);

      // Feedback de sucesso
      toast({ title: "Sucesso!", description: `${selectedTipo.replace('_', ' ')} registrado com sucesso!`, variant: "default" });
      
      // Vibração se disponível
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200]);
      }

      // 🔄 IMPORTANTE: Invalidar localização após registro bem-sucedido
      // Isso força busca de nova localização no próximo registro
      console.log('🔄 Invalidando localização em cache após registro bem-sucedido');
      setLocation(null);
      setGeoStatus('idle');

      // Resetar estados
      setCapturedPhoto(null);
      setSelectedTipo(null);
      setSelectedAcao(null);
      setShowRegistroModal(false);

    } catch (error) {
      console.error('Erro completo ao registrar ponto:', error);
      console.error('Response data:', error.response?.data);
      console.error('Status:', error.response?.status);
      
      // Verificar se precisa alterar a senha
      if (error.response?.status === 403 && 
          error.response?.data?.detail?.includes('alterar sua senha')) {
        setShowPasswordChangeModal(true);
        setRegistrandoPonto(false);
        return;
      }
      
      let errorMsg = 'Erro ao registrar ponto';
      let errorDetails = [];
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Se detail é um objeto com message, erros, restricoes
        if (typeof detail === 'object' && detail.message) {
          errorMsg = detail.message;
          
          // Adicionar erros específicos
          if (detail.erros && Array.isArray(detail.erros)) {
            errorDetails = detail.erros;
          }
          
          // Adicionar restrições
          if (detail.restricoes && Array.isArray(detail.restricoes)) {
            errorDetails = [...errorDetails, ...detail.restricoes];
          }
        }
        // Se detail é um array de erros de validação
        else if (Array.isArray(detail)) {
          const erros = detail.map(err => err.msg || err).join(', ');
          errorMsg = `Erro de validação: ${erros}`;
        } 
        // Se detail é uma string simples
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
        // Se detail tem propriedade message diretamente
        else if (detail.message) {
          errorMsg = detail.message;
        }
      } else if (error.response?.status === 401) {
        errorMsg = 'Não autorizado. Faça login novamente.';
      } else if (error.response?.status === 400) {
        errorMsg = 'Dados inválidos para registro';
      } else if (error.response?.status === 422) {
        errorMsg = 'Erro de validação nos dados enviados';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      // Montar mensagem completa
      let fullMessage = errorMsg;
      if (errorDetails.length > 0) {
        fullMessage += '\n\nDetalhes:\n• ' + errorDetails.join('\n• ');
      }
      
      toast({ 
        title: "Registro não aprovado", 
        description: fullMessage, 
        variant: "destructive",
        duration: 8000 // 8 segundos para dar tempo de ler todos os detalhes
      });
    } finally {
      setRegistrandoPonto(false);
    }
  };

  // NOVO: Buscar registros do dia para correção
  const buscarRegistrosParaCorrigir = async (data) => {
    if (!data || !user?.employee_id) return;
    
    setBuscandoRegistros(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/registros/${user.employee_id}?data_inicio=${data}&data_fim=${data}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Registros encontrados para correção:', response.data);
      
      // CORREÇÃO: API retorna objeto {funcionario_id, funcionario_nome, registros: [...]}
      // Precisamos acessar response.data.registros
      const registros = Array.isArray(response.data.registros) ? response.data.registros : 
                       Array.isArray(response.data) ? response.data : [];
      setRegistrosParaCorrigir(registros);
      
      if (registros.length === 0) {
        toast({
          title: "Nenhum registro encontrado",
          description: `Não há registros de ponto em ${data}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erro ao buscar registros:', error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar os registros. Tente novamente.",
        variant: "destructive"
      });
      setRegistrosParaCorrigir([]);
    } finally {
      setBuscandoRegistros(false);
    }
  };

  // NOVO: Solicitar ajuste UNIFICADO (ponto esquecido, abono ou banco de horas)
  const handleSolicitarAjuste = async () => {
    // Validações por tipo
    if (tipoSolicitacao === 'ponto_esquecido') {
      if (!ajusteData || !ajusteHora || !ajusteTipo || !ajusteJustificativa.trim()) {
        toast({
          title: "Atenção",
          description: "Preencha todos os campos obrigatórios",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'correcao') {
      if (!ajusteData || !registroSelecionado || !ajusteHora || !ajusteJustificativa.trim()) {
        toast({
          title: "Atenção",
          description: "Preencha todos os campos: selecione a data, o registro a corrigir, informe a hora correta e a justificativa",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'abono_falta') {
      if (!ajusteData || !justificativaId || !ajusteJustificativa.trim()) {
        toast({
          title: "Atenção",
          description: "Preencha todos os campos obrigatórios",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'banco_horas') {
      if (!ajusteData || !horasAbono || !ajusteJustificativa.trim()) {
        toast({
          title: "Atenção",
          description: "Preencha todos os campos obrigatórios",
          variant: "destructive"
        });
        return;
      }
    }

    if (!user || !user.employee_id) {
      toast({
        title: "Erro",
        description: "Funcionário não vinculado",
        variant: "destructive"
      });
      return;
    }

    setEnviandoAjuste(true);

    try {
      const token = localStorage.getItem('token');
      
      if (tipoSolicitacao === 'ponto_esquecido') {
        const payload = {
          funcionario_id: user.employee_id,
          data: ajusteData,
          hora: ajusteHora,
          tipo_ponto: ajusteTipo,
          justificativa: ajusteJustificativa
        };

        if (editandoAjusteId) {
          // Edição de solicitação pendente existente
          await axios.put(
            `${API}/api/timetracking/solicitacoes-ponto-esquecido/${editandoAjusteId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast({
            title: "Solicitação Atualizada!",
            description: "Sua correção será analisada pelo RH.",
          });
        } else {
        // Criação (comportamento original)
        await axios.post(
          `${API}/api/timetracking/solicitar-ponto-esquecido`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Ponto Esquecido Solicitado!",
          description: "Sua solicitação será analisada pelo RH.",
        });
        }
        
      } else if (tipoSolicitacao === 'correcao') {
        // Usar registro selecionado pelo usuário
        const dataHoraOriginal = new Date(registroSelecionado.data_hora);
        const horaOriginal = dataHoraOriginal.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        
        // Criar nova data/hora para correção
        const dataHoraNovaStr = `${ajusteData}T${ajusteHora}:00`;
        
        console.log('📤 Enviando correção:', {
          registro_id: registroSelecionado.id,
          hora_original: horaOriginal,
          hora_nova: ajusteHora,
          tipo: registroSelecionado.tipo_marcacao
        });
        
        // Solicitar correção
        await axios.post(
          `${API}/api/timetracking/ajustes`,
          {
            registro_ponto_id: registroSelecionado.id,
            funcionario_id: user.employee_id,
            tipo_ajuste: 'ALTERACAO',  // CORRIGIDO: usar enum válido
            data_hora_nova: dataHoraNovaStr,
            justificativa: ajusteJustificativa
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('✅ Correção enviada com sucesso!');
        
        toast({
          title: "Correção Solicitada!",
          description: `Correção de ${horaOriginal} para ${ajusteHora} solicitada. Aguarde aprovação do RH.`,
        });
        
      } else {
        // Novo endpoint de abonos (falta ou banco de horas)
        // Processar horasAbono no formato "HH:MM-HH:MM" ou deixar vazio
        let hora_inicio = null;
        let hora_fim = null;
        
        if (horasAbono && horasAbono.includes('-')) {
          const [inicio, fim] = horasAbono.split('-');
          hora_inicio = inicio;
          hora_fim = fim;
        }
        
        await axios.post(
          `${API}/api/abonos`,
          {
            funcionario_id: user.employee_id,
            tipo_abono: tipoSolicitacao === 'abono_falta' ? 'ABONO_FALTA' : 'BANCO_HORAS',
            data_inicio: ajusteData,
            data_fim: dataFimAbono || ajusteData,
            hora_inicio: hora_inicio,
            hora_fim: hora_fim,
            justificativa_id: justificativaId || null,
            justificativa_texto: ajusteJustificativa
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (tipoSolicitacao === 'abono_falta') {
          toast({
            title: "Abono Solicitado!",
            description: "Sua solicitação de abono será analisada pelo RH.",
          });
        } else {
          toast({
            title: "Compensação Solicitada!",
            description: "Sua solicitação de banco de horas será analisada.",
          });
        }
      }

      // Resetar form e fechar modal
      setAjusteData('');
      setAjusteHora('');
      setAjusteTipo('entrada');
      setAjusteJustificativa('');
      setJustificativaId('');
      setHorasAbono('');
      setDataFimAbono('');
      setTipoSolicitacao('ponto_esquecido');
      setRegistrosParaCorrigir([]); // Reset registros de correção
      setRegistroSelecionado(null); // Reset registro selecionado
      setShowAjusteModal(false);
      setShowOtherOptions(false);
      setEditandoAjusteId(null);

      // Recarregar registros e solicitações
      await buscarRegistrosHoje();
      await buscarSolicitacoesPendentes();

    } catch (error) {
      console.error('Erro ao solicitar ajuste:', error);
      
      // Extrair mensagem de erro do backend
      let mensagemErro = "Erro ao enviar solicitação";
      if (error.response?.data?.detail) {
        // Se detail é um array (erro de validação Pydantic)
        if (Array.isArray(error.response.data.detail)) {
          mensagemErro = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          mensagemErro = error.response.data.detail;
        }
      }
      
      toast({
        title: "Erro",
        description: mensagemErro,
        variant: "destructive"
      });
    } finally {
      setEnviandoAjuste(false);
    }
  };


  // Determinar próximo tipo de registro permitido baseado nos registros existentes
  // NOTA: Esta lógica foi movida para obterProximoTipoPermitido() acima
  
  // Tipos de marcação disponíveis
  const tiposMarcacao = [
    { 
      id: 'ENTRADA', 
      label: 'Entrada', 
      icon: LogIn, 
      color: 'bg-green-500 hover:bg-green-600',
      description: 'Registrar entrada no trabalho' 
    },
    { 
      id: 'SAIDA_INTERVALO', 
      label: 'Saída Intervalo', 
      icon: Coffee, 
      color: 'bg-yellow-500 hover:bg-yellow-600',
      description: 'Saída para almoço/intervalo' 
    },
    { 
      id: 'RETORNO_INTERVALO', 
      label: 'Retorno Intervalo', 
      icon: RotateCcw, 
      color: 'bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600',
      description: 'Retorno do almoço/intervalo' 
    },
    { 
      id: 'SAIDA', 
      label: 'Saída', 
      icon: LogOut, 
      color: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      description: 'Saída final do trabalho' 
    }
  ];

  // ============ TELA DE LOGIN PWA ============
  const LoginPWA = () => {
    const [formData, setFormData] = useState({
      email: '',
      password: ''
    });
    const [loggingIn, setLoggingIn] = useState(false);
    
    // Forçar sincronização DOM com React state
    useEffect(() => {
      const button = document.getElementById('pwa-login-button');
      if (button && !loggingIn) {
        button.removeAttribute('disabled');
      }
    }, [loggingIn]);

    const handleLogin = async (e) => {
      e.preventDefault();
      
      console.log('🔐 PWA Login iniciado:', formData.email);
      
      if (!formData.email || !formData.password) {
        console.error('❌ Campos obrigatórios não preenchidos');
        alert('Preencha email e senha'); // Usar alert como fallback
        return;
      }

      try {
        setLoggingIn(true);
        console.log('📡 Usando AuthContext login...');
        
        const result = await login(formData.email, formData.password);

        if (result.success) {
          console.log('✅ Login realizado com sucesso via AuthContext');
          console.log('🔑 Token e usuário salvos automaticamente');
          alert('Login realizado! Redirecionando...'); // Usar alert como fallback
          
          // O AuthContext já atualizou o user state, então não precisamos fazer nada mais
          console.log('📱 Liberando acesso ao PWA');
          setLoginRequired(false);
        } else {
          console.error('❌ Falha no login:', result.error);
          alert(`Erro no Login: ${result.error}`);
        }

      } catch (error) {
        console.error('❌ Erro no login PWA:', error);
        const errorMsg = error.response?.data?.detail || error.message || "Credenciais inválidas";
        alert(`Erro no Login: ${errorMsg}`); // Usar alert como fallback
      } finally {
        setLoggingIn(false);
        console.log('🔄 Login finalizado, loading = false');
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-10 w-10 text-blue-600" />
            </div>
            <CardTitle className="text-3xl font-bold text-gray-800">GridProfile</CardTitle>
            <CardTitle className="text-lg font-semibold text-blue-600">Registro de Ponto</CardTitle>
            <p className="text-gray-600 mt-2">Faça login para acessar o sistema</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="seu.email@empresa.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <input
                  type="password"
                  placeholder="Sua senha"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    password: e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <button
                id="pwa-login-button"
                type="submit"
                disabled={loggingIn}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
                style={{ pointerEvents: 'auto' }}
              >
                {loggingIn ? (
                  <>
                    <RotateCw className="h-5 w-5 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5 mr-2" />
                    Entrar
                  </>
                )}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center justify-center text-blue-800 text-sm">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="font-medium">Login obrigatório para registro de ponto</span>
                </div>
                <p className="text-blue-600 text-xs mt-1">
                  Entre em contato com o RH se tiver dificuldades
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Se login é necessário ou usuário não está logado, mostrar tela de login
  if (loginRequired || !user) {
    return <LoginPWA />;
  }

  // Se usuário logado mas não tem employee_id, mostrar erro específico
  if (!user.employee_id) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-orange-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mb-4">
              <X className="h-10 w-10 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-800">Funcionário não vinculado</CardTitle>
            <p className="text-gray-600 mt-2">Sua conta não está vinculada a um funcionário</p>
          </CardHeader>
          <CardContent className="text-center">
            <div className="bg-red-50 p-4 rounded-lg mb-4">
              <p className="text-red-800 font-medium">
                Usuário: {user.name || user.email}
              </p>
              <p className="text-red-600 text-sm mt-1">
                Employee ID não encontrado
              </p>
            </div>
            <p className="text-gray-600 mb-4">
              Entre em contato com o administrador para vincular sua conta a um funcionário no sistema.
            </p>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setLoginRequired(true);
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Fazer Logout
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-green-600">
      {/* Header Status */}
      <div className="flex items-center justify-between p-4 text-white text-sm">
        <div className="flex items-center space-x-2">
          <Smartphone className="h-4 w-4" />
          <span>GridProfile Ponto</span>
        </div>
        <div className="flex items-center space-x-3">
          {isOnline ? (
            <Wifi className="h-4 w-4 text-green-400" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-400" />
          )}
          <Signal className="h-4 w-4" />
          <Battery className="h-4 w-4" />
          {/* Botão de Logout no Header */}
          <button
            onClick={() => {
              if (window.confirm('Deseja realmente sair do sistema?')) {
                logout();
                toast({ title: "Sucesso!", description: "Logout realizado com sucesso!", variant: "default" });
              }
            }}
            className="ml-2 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
            title="Sair do sistema"
          >
            <LogOut className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Header com Logo e Relógio */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between">
          {/* Logo GridProfile */}
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl border border-white/30 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-green-400 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">GridProfile</h1>
              <p className="text-xs text-white/80">Ponto Digital</p>
            </div>
          </div>
          
          {/* Hora Digital */}
          <div className="text-right">
            <div className="text-3xl font-bold font-mono text-white">
              {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-xs text-white/80">
              {currentTime.toLocaleDateString('pt-BR', { 
                day: '2-digit',
                month: 'short'
              })}
            </div>
          </div>
        </div>
        
        {/* NOVO: Saldo de Banco de Horas */}
        {saldoBancoHoras && !carregandoSaldo && (
          <div className="mt-3 px-4">
            <div className={`
              flex items-center justify-between 
              px-4 py-3 rounded-lg backdrop-blur-sm
              ${
                saldoBancoHoras.saldo_acumulado_horas > 0 
                  ? 'bg-green-500/20 border border-green-400/30' 
                  : saldoBancoHoras.saldo_acumulado_horas < 0 
                  ? 'bg-red-500/20 border border-red-400/30'
                  : 'bg-gray-500/20 border border-gray-400/30'
              }
            `}>
              <div className="flex items-center gap-2">
                <Clock className={`
                  w-5 h-5
                  ${
                    saldoBancoHoras.saldo_acumulado_horas > 0 
                      ? 'text-green-300' 
                      : saldoBancoHoras.saldo_acumulado_horas < 0 
                      ? 'text-red-300'
                      : 'text-gray-300'
                  }
                `} />
                <span className="text-sm font-medium text-white/90">
                  Banco de Horas
                </span>
              </div>
              <div className={`
                text-xl font-bold font-mono
                ${
                  saldoBancoHoras.saldo_acumulado_horas > 0 
                    ? 'text-green-300' 
                    : saldoBancoHoras.saldo_acumulado_horas < 0 
                    ? 'text-red-300'
                    : 'text-gray-300'
                }
              `}>
        {formatarHoras(saldoBancoHoras.saldo_acumulado_horas)}
              </div>
            </div>
          </div>
        )}
        
        {carregandoSaldo && (
          <div className="mt-3 px-4">
            <div className="flex items-center justify-center px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm">
              <span className="text-sm text-white/70">Carregando saldo...</span>
            </div>
          </div>
        )}
      </div>

      {/* Seção Principal - Foto e Informações do Funcionário */}
      <div className="px-4 py-4">
        <Card className="bg-white bg-opacity-10 backdrop-blur-lg border-white border-opacity-20 shadow-2xl">
          <CardContent className="p-6">

            {/* Foto e Informações do Colaborador */}
            {dadosFuncionario && (
              <div className="bg-white bg-opacity-20 rounded-2xl p-5 backdrop-blur-sm">
                {/* Foto do Funcionário */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white">
                      {dadosFuncionario.foto ? (
                        <img 
                          src={dadosFuncionario.foto} 
                          alt={dadosFuncionario.nome}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                          <span className="text-4xl font-bold text-white">
                            {dadosFuncionario.nome?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 border-2 border-white shadow-md">
                      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Informações */}
                <div className="space-y-3 text-white">
                  {/* Nome */}
                  <div className="bg-white bg-opacity-20 rounded-xl p-3 backdrop-blur-sm">
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs opacity-75 mb-1">Nome</div>
                        <div className="font-bold text-lg">{dadosFuncionario.nome || user?.name}</div>
                      </div>
                    </div>
                  </div>

                  {/* Cargo */}
                  <div className="bg-white bg-opacity-20 rounded-xl p-3 backdrop-blur-sm">
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs opacity-75 mb-1">Cargo</div>
                        <div className="font-semibold">{dadosFuncionario.cargo || 'Não informado'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Data de Admissão */}
                  <div className="bg-white bg-opacity-20 rounded-xl p-3 backdrop-blur-sm">
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs opacity-75 mb-1">Data de Admissão</div>
                        <div className="font-semibold">
                          {dadosFuncionario.admissao 
                            ? new Date(dadosFuncionario.admissao).toLocaleDateString('pt-BR')
                            : 'Não informado'}
                        </div>
                        {dadosFuncionario.admissao && (
                          <div className="text-xs mt-1 opacity-75">
                            {(() => {
                              const admissao = new Date(dadosFuncionario.admissao);
                              const hoje = new Date();
                              const anos = hoje.getFullYear() - admissao.getFullYear();
                              const meses = hoje.getMonth() - admissao.getMonth();
                              const totalMeses = anos * 12 + meses;
                              const anosCompletos = Math.floor(totalMeses / 12);
                              const mesesRestantes = totalMeses % 12;
                              
                              if (anosCompletos > 0) {
                                return `${anosCompletos} ano${anosCompletos > 1 ? 's' : ''} e ${mesesRestantes} ${mesesRestantes !== 1 ? 'meses' : 'mês'} na empresa`;
                              } else {
                                return `${mesesRestantes} ${mesesRestantes !== 1 ? 'meses' : 'mês'} na empresa`;
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state se dados ainda não carregados */}
            {!dadosFuncionario && (
              <div className="text-center py-4 text-white">
                <div className="animate-pulse">
                  <div className="h-32 w-32 bg-white bg-opacity-20 rounded-full mx-auto mb-4"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded w-1/2 mx-auto"></div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status da Localização */}
      <div className="px-4 mb-6">
        <Card className="bg-white bg-opacity-10 backdrop-blur-sm border-white border-opacity-20 text-white">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <MapPin className="h-5 w-5" />
              <div>
                <div className="font-medium">Status da Localização</div>
                <div className="text-sm opacity-90">
                  {geoStatus === 'checking' && 'Verificando GPS...'}
                  {geoStatus === 'requesting' && 'Solicitando permissão...'}
                  {geoStatus === 'success' && '✓ Localização confirmada'}
                  {geoStatus === 'error' && '✗ Erro na localização'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NOVO: Status GPS e Controles */}
      {location && (
        <div className="px-6 mb-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Navigation className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">Status GPS</span>
                </div>
                <Badge className={`${
                  location.accuracy < 20 ? 'bg-green-500' :
                  location.accuracy < 50 ? 'bg-yellow-500' :
                  'bg-orange-500'
                }`}>
                  {location.accuracy?.toFixed(0)}m
                </Badge>
              </div>
              
              <div className="text-sm text-gray-700 mb-3">
                <div>📍 Lat: {location.latitude?.toFixed(6)}</div>
                <div>📍 Lng: {location.longitude?.toFixed(6)}</div>
                {location.fromMultipleReadings && (
                  <div className="text-green-600 font-semibold mt-1">
                    ✓ {location.numReadings} leituras (alta precisão)
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={recalibrarGPS}
                  disabled={recalibrando}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <Target className="h-4 w-4 mr-2" />
                  {recalibrando ? 'Recalibrando...' : 'Recalibrar GPS'}
                </button>
                
                <button
                  onClick={() => setShowMapModal(true)}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Map className="h-4 w-4 mr-2" />
                  Ver no Mapa
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botões Principais */}
      <div className="px-6 space-y-4">
        {/* Botão 1: Registrar Ponto - Azul Marinho Neon */}
        <Button
          onClick={() => setShowRegistroModal(true)}
          className="w-full h-20 text-2xl font-bold bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white border-2 border-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_rgba(59,130,246,0.8)] transition-all duration-300"
          disabled={!isOnline}
        >
          <Clock className="h-8 w-8 mr-4" />
          Registrar Ponto
        </Button>

        {/* Botão 2: Outras Opções - Verde Neon */}
        <Button
          onClick={() => setShowOtherOptions(true)}
          className="w-full h-16 text-xl bg-gradient-to-r from-green-700 to-green-600 hover:from-green-600 hover:to-green-500 text-white border-2 border-green-400 shadow-[0_0_20px_rgba(34,197,94,0.5)] hover:shadow-[0_0_30px_rgba(34,197,94,0.8)] transition-all duration-300"
        >
          <Menu className="h-6 w-6 mr-3" />
          Outras Opções
        </Button>
      </div>

      {/* Informações do Usuário */}
      <div className="px-4 mt-8 mb-4">
        <Card className="bg-white bg-opacity-10 backdrop-blur-sm border-white border-opacity-20 text-white">
          <CardContent className="p-4">
            <div className="text-center mb-3">
              <div className="text-sm opacity-90">REP-P | Portaria 671/2021</div>
              <div className="text-xs mt-2 opacity-75">
                {isOnline ? '🟢 Online' : '🔴 Offline'}
              </div>
            </div>
            
            {/* Botão de Logout - Vermelho Neon */}
            <Button
              onClick={() => {
                if (window.confirm('Deseja realmente sair do sistema?')) {
                  logout();
                  toast({ title: "Sucesso!", description: "Logout realizado com sucesso!", variant: "default" });
                }
              }}
              variant="outline"
              className="w-full mt-3 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white border-2 border-red-400 shadow-[0_0_20px_rgba(239,68,68,0.5)] hover:shadow-[0_0_30px_rgba(239,68,68,0.8)] transition-all duration-300"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair do Sistema
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Modal: Seleção de Tipo de Registro */}
      {showRegistroModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Registrar Ponto</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRegistroModal(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {!selectedTipo ? (
                <div className="space-y-3">
                  <p className="text-gray-600 mb-4">Selecione o tipo de registro:</p>
                  
                  {/* Mostrar próximo tipo permitido */}
                  {!carregandoRegistros && obterAcoesPermitidas().length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>Próxima ação:</strong> {obterAcoesPermitidas().map(a => a.label).join(' ou ')}
                      </p>
                    </div>
                  )}
                  
                  {/* Mostrar todos os pontos já registrados */}
                  {obterAcoesPermitidas().length === 0 && (
                    <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 rounded">
                      <p className="text-sm text-green-800">
                        ✅ Jornada encerrada ou limite diário atingido.
                      </p>
                    </div>
                  )}
                  
                  {/* Indicador de Geofence */}
                  {geoStatus === 'success' && !carregandoGeofences && dentroDaArea !== null && (
                    <div className={`mb-4 p-3 border-l-4 rounded ${
                      dentroDaArea 
                        ? 'bg-green-50 border-green-500' 
                        : 'bg-red-50 border-red-500'
                    }`}>
                      {dentroDaArea ? (
                        <>
                          <p className="text-sm text-green-800 font-semibold">
                            ✅ Dentro da área permitida
                          </p>
                          {geofenceMaisProxima && (
                            <p className="text-xs text-green-700 mt-1">
                              📍 {geofenceMaisProxima.nome} ({distanciaMinima.toFixed(0)}m)
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-red-800 font-semibold">
                            ❌ Fora da área permitida
                          </p>
                          {geofenceMaisProxima && (
                            <p className="text-xs text-red-700 mt-1">
                              📍 Mais próximo: {geofenceMaisProxima.nome} ({distanciaMinima.toFixed(0)}m de distância)
                            </p>
                          )}
                          <p className="text-xs text-red-700 mt-1">
                            ⚠️ Você precisa estar dentro de uma área autorizada para registrar ponto
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Mensagem quando não tem geofence cadastrada */}
                  {geoStatus === 'success' && !carregandoGeofences && geofencesDoColaborador.length === 0 && (
                    <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
                      <p className="text-sm text-amber-800 font-semibold">
                        ⚠️ Nenhuma área de trabalho cadastrada
                      </p>
                      <p className="text-xs text-amber-700 mt-2">
                        Você não possui nenhuma cerca geográfica (geofence) vinculada ao seu cadastro.
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        <strong>Entre em contato com o administrador do sistema</strong> para solicitar o cadastro da sua área de trabalho autorizada.
                      </p>
                    </div>
                  )}
                  
                  {/* Mostrar apenas o próximo tipo permitido ou mensagem de completo */}
                  {(() => {
                    const acoesPermitidas = obterAcoesPermitidas();
                    
                    if (acoesPermitidas.length === 0) {
                      return (
                        <div className="text-center py-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-2xl text-white">
                          <CheckCircle className="h-16 w-16 mx-auto mb-4" />
                          <h3 className="text-2xl font-bold mb-2">Registros Completos!</h3>
                          <p className="text-lg">Bom Descanso! 😊</p>
                          <p className="text-sm mt-2 opacity-90">
                            A jornada foi encerrada e não aceita novas batidas.
                          </p>
                        </div>
                      );
                    }
                    
                    const bloqueadoPorGeofence = dentroDaArea === false;
                    
                    return (
                      <div className="space-y-3">
                        {/* Info sobre o próximo registro */}
                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                          <p className="text-sm text-blue-800">
                            <strong>Escolha a próxima ação:</strong>
                          </p>
                          <p className="text-lg font-bold text-blue-900 mt-1">
                            {acoesPermitidas.map(a => a.label).join(' ou ')}
                          </p>
                        </div>
                        
                        {/* Botão grande do próximo tipo */}
                        {acoesPermitidas.map(acao => {
                          const tipoParaMostrar = tiposMarcacao.find(t => t.id === acao.tipo);
                          const IconComponent = tipoParaMostrar?.icon || Clock;
                          return (
                            <Button
                              key={`${acao.tipo}-${acao.acao}`}
                              onClick={() => {
                                if (bloqueadoPorGeofence) {
                                  toast({ title: "Erro", description: "Você está fora da área permitida para registrar ponto", variant: "destructive" });
                                  return;
                                }
                                setSelectedAcao(acao.acao);
                                setSelectedTipo(acao.tipo);
                              }}
                              disabled={bloqueadoPorGeofence}
                              className={`w-full h-auto py-6 px-4 text-left ${
                                bloqueadoPorGeofence ? 'bg-gray-300 cursor-not-allowed' : tipoParaMostrar?.color
                              } text-white transition-all transform hover:scale-105 shadow-lg`}
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 mr-4"><IconComponent className="h-10 w-10" /></div>
                                <div className="flex-1">
                                  <div className="font-bold text-xl mb-1">{acao.label}</div>
                                  <div className="text-sm opacity-90">{tipoParaMostrar?.description}</div>
                                </div>
                              </div>
                            </Button>
                          );
                        })}
                        
                        {/* Informação sobre registros já feitos */}
                        {registrosHoje.length > 0 && (
                          <div className="bg-gray-50 rounded p-3">
                            <p className="text-xs text-gray-600 font-semibold mb-2">
                              ✅ Registros realizados hoje ({registrosHoje.length}):
                            </p>
                            <div className="space-y-1">
                              {registrosHoje.map((reg, idx) => {
                                const tipoReg = reg.tipo_marcacao || reg.tipo;
                                const horario = new Date(reg.data_hora).toLocaleTimeString('pt-BR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                                return (
                                  <div key={idx} className="text-xs text-gray-700 flex items-center">
                                    <CheckCircle className="h-3 w-3 text-green-600 mr-2" />
                                    <span className="font-medium">{LABEL_MARCACOES[tipoReg]}</span>
                                    <span className="ml-auto text-gray-500">{horario}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Removido o mapeamento de todos os tipos - agora mostra apenas o próximo 
                  {tiposMarcacao.map((tipo) => {
                    ...código antigo removido...
                  })}
                  */}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Cabeçalho com tipo de marcação */}
                  <div className="text-center bg-gradient-to-r from-blue-500 to-green-500 text-white p-4 rounded-lg shadow-lg">
                    <h3 className="text-2xl font-bold mb-2">
                      {tiposMarcacao.find(t => t.id === selectedTipo)?.label}
                    </h3>
                    <p className="text-sm opacity-90">
                      📸 Tire uma selfie para comprovar sua presença
                    </p>
                  </div>

                  {/* Captura de Foto */}
                  <div className="space-y-4">
                    {!showCamera && !capturedPhoto && (
                      <div className="text-center py-8">
                        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-gray-600 font-medium">Preparando câmera frontal...</p>
                        <p className="text-sm text-gray-500 mt-2">A câmera abrirá automaticamente</p>
                      </div>
                    )}

                    {showCamera && (
                      <div className="space-y-4">
                        {/* Preview da câmera com design moderno */}
                        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '3/4' }}>
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                            onClick={tryManualPlay}
                          />
                          
                          {/* Overlay de carregamento modernizado */}
                          {loadingCamera && (
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-green-600 bg-opacity-95 flex items-center justify-center">
                              <div className="text-white text-center space-y-6">
                                <div className="relative">
                                  <div className="animate-spin w-16 h-16 border-4 border-white border-t-transparent rounded-full mx-auto"></div>
                                  <Camera className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white" />
                                </div>
                                <div>
                                  <p className="text-lg font-semibold">Ativando câmera...</p>
                                  <p className="text-sm opacity-90 mt-1">Aguarde alguns instantes</p>
                                </div>
                                
                                {/* Botão de emergência após carregamento */}
                                {!cameraReady && (
                                  <button 
                                    onClick={forceActivateCamera}
                                    className="mt-4 px-6 py-3 bg-white text-blue-600 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all shadow-lg"
                                  >
                                    ⚡ Ativar Manualmente
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Indicadores de status no topo */}
                          <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                            <div className="bg-black bg-opacity-60 backdrop-blur-sm text-white px-3 py-2 rounded-full text-xs font-medium flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                              {facingMode === 'user' ? '🤳 Câmera Frontal' : '📷 Câmera Traseira'}
                            </div>
                            
                            {cameraReady && (
                              <div className="bg-green-500 backdrop-blur-sm text-white px-3 py-2 rounded-full text-xs font-medium flex items-center gap-2 animate-fade-in">
                                <CheckCircle className="w-3 h-3" />
                                Pronta
                              </div>
                            )}
                          </div>
                          
                          {/* Guia de enquadramento */}
                          {cameraReady && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="border-4 border-white border-dashed rounded-full w-64 h-64 opacity-30"></div>
                            </div>
                          )}
                        </div>
                        
                        {/* Controles da câmera com design moderno */}
                        <div className="grid grid-cols-3 gap-3">
                          <Button
                            onClick={switchCamera}
                            variant="outline"
                            className="flex flex-col items-center justify-center py-4 border-2 hover:border-blue-500 hover:bg-blue-50 transition-all"
                            disabled={!cameraReady || loadingCamera}
                          >
                            <RotateCw className="h-6 w-6 mb-1" />
                            <span className="text-xs font-medium">Inverter</span>
                          </Button>
                          <Button
                            onClick={capturePhoto}
                            className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white flex flex-col items-center justify-center py-4 shadow-lg transform hover:scale-105 transition-all"
                            disabled={!cameraReady || loadingCamera}
                          >
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-1">
                              <Camera className="h-6 w-6 text-blue-500" />
                            </div>
                            <span className="text-xs font-semibold">{cameraReady ? 'Capturar' : 'Aguarde...'}</span>
                          </Button>
                          <Button
                            onClick={() => {
                              stopCamera();
                              setSelectedTipo(null);
                              setSelectedAcao(null);
                            }}
                            variant="outline"
                            className="flex flex-col items-center justify-center py-4 border-2 hover:border-red-500 hover:bg-red-50 transition-all"
                          >
                            <X className="h-6 w-6 mb-1" />
                            <span className="text-xs font-medium">Cancelar</span>
                          </Button>
                        </div>
                        
                        {/* Instruções visuais */}
                        <div className="text-center bg-blue-50 p-4 rounded-lg border border-blue-100">
                          {loadingCamera && (
                            <p className="text-sm text-blue-700 font-medium flex items-center justify-center gap-2">
                              <span className="animate-pulse">⏳</span> Aguardando câmera carregar...
                            </p>
                          )}
                          {cameraReady && (
                            <p className="text-sm text-green-700 font-medium flex items-center justify-center gap-2">
                              <span>✅</span> Posicione seu rosto no centro e clique em Capturar
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {capturedPhoto && (
                      <div className="space-y-4">
                        {/* Preview da foto capturada */}
                        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl">
                          <img
                            src={capturedPhoto}
                            alt="Foto capturada"
                            className="w-full h-auto object-cover"
                          />
                          <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-2 rounded-full text-xs font-medium flex items-center gap-2 shadow-lg">
                            <CheckCircle className="w-4 h-4" />
                            Foto OK
                          </div>
                        </div>
                        
                        {/* Mensagem de sucesso */}
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                          <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-semibold">Selfie capturada com sucesso!</p>
                          <p className="text-green-600 text-sm mt-1">Você pode confirmar ou tirar uma nova foto</p>
                        </div>
                        
                        {/* Botões de ação */}
                        <div className="flex gap-3">
                          <Button
                            onClick={registrarPontoComFoto}
                            disabled={registrandoPonto}
                            className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white h-14 text-base font-semibold shadow-lg"
                          >
                            {registrandoPonto ? (
                              <>
                                <span className="animate-spin mr-2">⏳</span>
                                Registrando...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-5 w-5 mr-2" />
                                Confirmar Registro
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => {
                              setCapturedPhoto(null);
                              setShowCamera(true);
                              setTimeout(() => startCamera(), 300);
                            }}
                            variant="outline"
                            className="flex-1 h-14 border-2 hover:border-blue-500"
                          >
                            <Camera className="h-5 w-5 mr-2" />
                            Nova Foto
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botão voltar */}
                  <Button
                    onClick={() => {
                      setSelectedTipo(null);
                      setSelectedAcao(null);
                      setCapturedPhoto(null);
                      stopCamera();
                    }}
                    variant="ghost"
                    className="w-full hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar para tipos de marcação
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Outras Opções */}
      {showOtherOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Outras Opções</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOtherOptions(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Grid de Opções */}
              <div className="space-y-3">
                {/* Histórico de pontos */}
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    setShowHistorico(true);
                    buscarHistoricoCompleto();
                  }}
                >
                  <Clock className="h-8 w-8" />
                  <div className="text-left">
                    <div className="font-semibold text-lg">Histórico de pontos</div>
                    <div className="text-sm opacity-90">Ver todos os registros</div>
                  </div>
                </button>

                {/* Folha de ponto */}
                <button
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-green-600 hover:to-green-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    window.location.href = '/ponto-pwa/folha-ponto';
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Folha de ponto</div>
                    <div className="text-sm opacity-90">Visualizar e assinar</div>
                  </div>
                </button>

                {/* Documentos Assinados */}
                <button
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-teal-600 hover:to-teal-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    window.location.href = '/ponto-pwa/documentos';
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17h6M9 13h6M9 9h1" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Documentos Assinados</div>
                    <div className="text-sm opacity-90">Baixar PDFs</div>
                  </div>
                </button>

                {/* Solicitar Ajuste */}
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-purple-600 hover:to-purple-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    carregarJustificativas(); // Carregar justificativas ao abrir
                    setTipoSolicitacao('ponto_esquecido'); // Reset para tipo padrão
                    setShowAjusteModal(true);
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Solicitar Ajuste</div>
                    <div className="text-sm opacity-90">Ponto, abono ou banco de horas</div>
                  </div>
                </button>

                {/* Ajustes Solicitados */}
                <button
                  className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    setShowAjustesSolicitados(true);
                    carregarAjustesSolicitados();
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Ajustes Solicitados</div>
                    <div className="text-sm opacity-90">Acompanhar status</div>
                  </div>
                </button>

                {/* Enviar Documentos */}
                <button
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-orange-600 hover:to-orange-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    setShowEnviarDocumento(true);
                    carregarTiposDocumentos();
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Enviar Documentos</div>
                    <div className="text-sm opacity-90">Atestados, comprovantes</div>
                  </div>
                </button>

                {/* Documentos Enviados */}
                <button
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-teal-600 hover:to-teal-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    setShowDocumentosEnviados(true);
                    carregarMeusDocumentos();
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Documentos Enviados</div>
                    <div className="text-sm opacity-90">Visualizar histórico</div>
                  </div>
                </button>

                {/* Solicitar Férias */}
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-purple-600 hover:to-purple-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    setShowSolicitarFerias(true);
                    carregarDadosFerias();
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Solicitar Férias</div>
                    <div className="text-sm opacity-90">Fazer pedido de férias</div>
                  </div>
                </button>

                {/* Registro de Viagem */}
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-blue-600 hover:to-blue-800 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    window.location.href = '/registro-viagem';
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Registro de Viagem</div>
                    <div className="text-sm opacity-90">Controle de quilometragem</div>
                  </div>
                </button>

                {/* Solicitações de ajustes */}
                <button
                  className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl p-4 flex items-center space-x-4 hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-md"
                  onClick={() => {
                    setShowOtherOptions(false);
                    toast({ title: "Sucesso!", description: "Solicitações - Em desenvolvimento", variant: "default" });
                  }}
                >
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold text-lg">Solicitações de ajustes</div>
                    <div className="text-sm opacity-90">Acompanhar status</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Canvas oculto para captura */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      


      {/* Modal: Solicitar Ajuste UNIFICADO */}
      {showAjusteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Solicitar Ajuste</h3>
              <button
                onClick={() => {
                  setShowAjusteModal(false);
                  // Reset form
                  setTipoSolicitacao('ponto_esquecido');
                  setEditandoAjusteId(null);
                  setAjusteData('');
                  setAjusteHora('');
                  setAjusteTipo('entrada');
                  setAjusteJustificativa('');
                  setJustificativaId('');
                  setHorasAbono('');
                  setDataFimAbono('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Seletor de Tipo de Solicitação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Solicitação *
                </label>
                <select
                  value={tipoSolicitacao}
                  onChange={(e) => setTipoSolicitacao(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                >
                  <option value="ponto_esquecido">📍 Ponto Esquecido</option>
                  <option value="correcao">✏️ Correção de Registro</option>
                  <option value="abono_falta">📋 Abono de Falta</option>
                  <option value="banco_horas">⏰ Banco de Horas</option>
                </select>
              </div>

              {/* Info Box Dinâmico */}
              <div className={`border rounded-lg p-3 ${
                tipoSolicitacao === 'ponto_esquecido' ? 'bg-blue-50 border-blue-200' :
                tipoSolicitacao === 'correcao' ? 'bg-purple-50 border-purple-200' :
                tipoSolicitacao === 'abono_falta' ? 'bg-orange-50 border-orange-200' :
                'bg-green-50 border-green-200'
              }`}>
                <p className={`text-sm ${
                  tipoSolicitacao === 'ponto_esquecido' ? 'text-blue-800' :
                  tipoSolicitacao === 'correcao' ? 'text-purple-800' :
                  tipoSolicitacao === 'abono_falta' ? 'text-orange-800' :
                  'text-green-800'
                }`}>
                  <strong>
                    {tipoSolicitacao === 'ponto_esquecido' && 'Ponto Esquecido:'}
                    {tipoSolicitacao === 'correcao' && 'Correção de Registro:'}
                    {tipoSolicitacao === 'abono_falta' && 'Abono de Falta:'}
                    {tipoSolicitacao === 'banco_horas' && 'Banco de Horas:'}
                  </strong>{' '}
                  {tipoSolicitacao === 'ponto_esquecido' && 'Use para criar um registro de ponto que você esqueceu de bater.'}
                  {tipoSolicitacao === 'correcao' && 'Corrija a hora de um registro de ponto já existente.'}
                  {tipoSolicitacao === 'abono_falta' && 'Solicite abono para ausências justificadas (atestado, licença, etc).'}
                  {tipoSolicitacao === 'banco_horas' && 'Compense horas negativas usando seu saldo positivo de banco de horas.'}
                </p>
              </div>

              {/* CAMPOS PONTO ESQUECIDO */}
              {tipoSolicitacao === 'ponto_esquecido' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data do Ponto Esquecido *
                    </label>
                    <input
                      type="date"
                      value={ajusteData}
                      onChange={(e) => setAjusteData(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora Exata *
                    </label>
                    <input
                      type="time"
                      value={ajusteHora}
                      onChange={(e) => setAjusteHora(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Ponto *
                    </label>
                    <select
                      value={ajusteTipo}
                      onChange={(e) => setAjusteTipo(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="entrada">Entrada</option>
                      <option value="saida_intervalo">Saída para Intervalo</option>
                      <option value="retorno_intervalo">Retorno do Intervalo</option>
                      <option value="saida">Saída</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Justificativa *
                    </label>
                    <textarea
                      value={ajusteJustificativa}
                      onChange={(e) => setAjusteJustificativa(e.target.value)}
                      placeholder="Explique por que esqueceu de registrar..."
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </>
              )}

              {/* CAMPOS CORREÇÃO DE REGISTRO - NOVO FLUXO */}
              {tipoSolicitacao === 'correcao' && (
                <>
                  {/* Passo 1: Selecionar Data */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      1. Selecione a Data do Registro *
                    </label>
                    <input
                      type="date"
                      value={ajusteData}
                      onChange={(e) => {
                        setAjusteData(e.target.value);
                        setRegistroSelecionado(null); // Reset registro selecionado
                        setAjusteHora(''); // Reset hora
                        if (e.target.value) {
                          buscarRegistrosParaCorrigir(e.target.value);
                        } else {
                          setRegistrosParaCorrigir([]);
                        }
                      }}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Passo 2: Selecionar Registro para Corrigir */}
                  {ajusteData && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        2. Selecione o Registro que Deseja Corrigir *
                      </label>
                      
                      {buscandoRegistros ? (
                        <div className="p-4 text-center text-gray-500">
                          Buscando registros...
                        </div>
                      ) : registrosParaCorrigir.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
                          Nenhum registro encontrado nesta data
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {registrosParaCorrigir.map((registro) => {
                            const dataHora = new Date(registro.data_hora);
                            const hora = dataHora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                            const tipoTexto = {
                              'ENTRADA': 'Entrada',
                              'SAIDA_INTERVALO': 'Saída para Intervalo',
                              'RETORNO_INTERVALO': 'Retorno do Intervalo',
                              'SAIDA': 'Saída'
                            }[registro.tipo_marcacao] || registro.tipo_marcacao;
                            
                            return (
                              <button
                                key={registro.id}
                                type="button"
                                onClick={() => {
                                  setRegistroSelecionado(registro);
                                  setAjusteHora(''); // Reset hora para forçar usuário a digitar nova
                                }}
                                className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                                  registroSelecionado?.id === registro.id
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span className="font-bold text-lg">{hora}</span>
                                    <span className="text-gray-600 ml-2">{tipoTexto}</span>
                                  </div>
                                  {registroSelecionado?.id === registro.id && (
                                    <span className="text-purple-600 font-medium">✓ Selecionado</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Passo 3: Digitar Hora Correta */}
                  {registroSelecionado && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        3. Digite a Hora Correta *
                      </label>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                        <p className="text-sm text-yellow-800">
                          <strong>Hora Atual:</strong> {new Date(registroSelecionado.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          <br />
                          <strong>Digite abaixo a hora correta:</strong>
                        </p>
                      </div>
                      <input
                        type="time"
                        value={ajusteHora}
                        onChange={(e) => setAjusteHora(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Ex: 09:00"
                      />
                    </div>
                  )}

                  {/* Passo 4: Justificativa */}
                  {registroSelecionado && ajusteHora && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        4. Justificativa *
                      </label>
                      <textarea
                        value={ajusteJustificativa}
                        onChange={(e) => setAjusteJustificativa(e.target.value)}
                        placeholder="Explique por que o horário precisa ser corrigido..."
                        rows={4}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                      />
                    </div>
                  )}
                </>
              )}

              {/* CAMPOS ABONO DE FALTA */}
              {tipoSolicitacao === 'abono_falta' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Início *
                    </label>
                    <input
                      type="date"
                      value={ajusteData}
                      onChange={(e) => setAjusteData(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Fim (deixe igual se for mesmo dia)
                    </label>
                    <input
                      type="date"
                      value={dataFimAbono || ajusteData}
                      onChange={(e) => setDataFimAbono(e.target.value)}
                      min={ajusteData}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Período do Abono (deixe vazio para dia inteiro)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Das (HH:MM)</label>
                        <input
                          type="time"
                          value={horasAbono.split('-')[0] || ''}
                          onChange={(e) => {
                            const fim = horasAbono.split('-')[1] || '';
                            setHorasAbono(e.target.value + (fim ? `-${fim}` : ''));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Até (HH:MM)</label>
                        <input
                          type="time"
                          value={horasAbono.split('-')[1] || ''}
                          onChange={(e) => {
                            const inicio = horasAbono.split('-')[0] || '';
                            setHorasAbono((inicio ? `${inicio}-` : '') + e.target.value);
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo do Abono *
                    </label>
                    <select
                      value={justificativaId}
                      onChange={(e) => setJustificativaId(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                    >
                      <option value="">Selecione...</option>
                      {justificativasFalta.map((just) => (
                        <option key={just.id} value={just.id}>
                          {just.nome}
                          {just.requer_documento && ' (requer documento)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Justificativa Adicional *
                    </label>
                    <textarea
                      value={ajusteJustificativa}
                      onChange={(e) => setAjusteJustificativa(e.target.value)}
                      placeholder="Descreva o motivo..."
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 resize-none"
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      ⚠️ <strong>Documento:</strong> Você já enviou o comprovante? Use o módulo "Enviar Documentos" antes de solicitar o abono.
                    </p>
                  </div>
                </>
              )}

              {/* CAMPOS BANCO DE HORAS */}
              {tipoSolicitacao === 'banco_horas' && (
                <>
                  {saldoBancoHoras && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Seu saldo atual:</strong>{' '}
                        <span className={`text-lg font-bold ${
                          saldoBancoHoras.saldo_acumulado_horas > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatarHoras(saldoBancoHoras.saldo_acumulado_horas)}
                        </span>
                      </p>
                      {saldoBancoHoras.saldo_acumulado_horas <= 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ Você não tem saldo positivo para compensar.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data a Compensar *
                    </label>
                    <input
                      type="date"
                      value={ajusteData}
                      onChange={(e) => setAjusteData(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Das (HH:MM) *
                      </label>
                      <input
                        type="time"
                        value={horasAbono.split('-')[0] || ''}
                        onChange={(e) => {
                          const fim = horasAbono.split('-')[1] || '';
                          setHorasAbono(e.target.value + (fim ? `-${fim}` : ''));
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Até (HH:MM) *
                      </label>
                      <input
                        type="time"
                        value={horasAbono.split('-')[1] || ''}
                        onChange={(e) => {
                          const inicio = horasAbono.split('-')[0] || '';
                          setHorasAbono((inicio ? `${inicio}-` : '') + e.target.value);
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Justificativa *
                    </label>
                    <textarea
                      value={ajusteJustificativa}
                      onChange={(e) => setAjusteJustificativa(e.target.value)}
                      placeholder="Explique o motivo da compensação..."
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                </>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAjusteModal(false);
                    setEditandoAjusteId(null);
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSolicitarAjuste}
                  disabled={enviandoAjuste}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all font-medium disabled:opacity-50"
                >
                  {enviandoAjuste ? 'Enviando...' : 'Enviar Solicitação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estilos personalizados para animações */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>

      {/* Modal: Histórico de Pontos */}
      {showHistorico && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">Histórico de Pontos</h2>
                <button
                  onClick={() => setShowHistorico(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-blue-100 text-sm">Filtre por período</p>
            </div>

            {/* Filtros de Período */}
            <div className="bg-gray-50 p-4 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => {
                    setFiltroHistorico('dia');
                    buscarHistoricoCompleto('dia');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtroHistorico === 'dia'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Hoje
                </button>
                <button
                  onClick={() => {
                    setFiltroHistorico('7dias');
                    buscarHistoricoCompleto('7dias');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtroHistorico === '7dias'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  7 dias
                </button>
                <button
                  onClick={() => {
                    setFiltroHistorico('15dias');
                    buscarHistoricoCompleto('15dias');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtroHistorico === '15dias'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  15 dias
                </button>
                <button
                  onClick={() => {
                    setFiltroHistorico('30dias');
                    buscarHistoricoCompleto('30dias');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtroHistorico === '30dias'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  30 dias
                </button>
              </div>

              {/* Período Personalizado */}
              <div className="bg-white rounded-lg p-3 border border-gray-300">
                <button
                  onClick={() => setFiltroHistorico('periodo')}
                  className={`w-full text-left font-medium mb-2 ${
                    filtroHistorico === 'periodo' ? 'text-blue-600' : 'text-gray-700'
                  }`}
                >
                  📅 Período Personalizado
                </button>
                
                {filtroHistorico === 'periodo' && (
                  <div className="space-y-2 mt-2">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Data Início</label>
                      <input
                        type="date"
                        value={dataInicioCustom}
                        onChange={(e) => setDataInicioCustom(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Data Fim</label>
                      <input
                        type="date"
                        value={dataFimCustom}
                        onChange={(e) => setDataFimCustom(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <button
                      onClick={() => buscarHistoricoCompleto('periodo')}
                      className="w-full bg-blue-600 text-white rounded-lg py-2 font-medium hover:bg-blue-700 transition-colors"
                    >
                      Aplicar Filtro
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {carregandoHistorico ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Carregando histórico...</p>
                </div>
              ) : historicoRegistros.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 text-lg mb-2">Nenhum registro encontrado</p>
                  <p className="text-gray-500 text-sm">Seus registros dos últimos 30 dias aparecerão aqui</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Agrupar por data */}
                  {Object.entries(
                    historicoRegistros.reduce((grupos, registro) => {
                      const data = registro.data_hora.split('T')[0];
                      if (!grupos[data]) grupos[data] = [];
                      grupos[data].push(registro);
                      return grupos;
                    }, {})
                  )
                    .sort(([dataA], [dataB]) => dataB.localeCompare(dataA))
                    .map(([data, registros]) => (
                      <div key={data} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-gray-900">
                            {new Date(data + 'T00:00:00').toLocaleDateString('pt-BR', { 
                              weekday: 'long', 
                              day: '2-digit', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </h3>
                          <span className="text-sm text-gray-500">{registros.length} registros</span>
                        </div>
                        
                        <div className="space-y-2">
                          {registros
                            .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
                            .map((registro, idx) => {
                              const tipoLabels = {
                                'ENTRADA': 'Entrada',
                                'SAIDA_INTERVALO': 'Saída Intervalo',
                                'RETORNO_INTERVALO': 'Retorno Intervalo',
                                'SAIDA': 'Saída'
                              };
                              
                              const tipoColors = {
                                'ENTRADA': 'bg-green-100 text-green-700 border-green-200',
                                'SAIDA_INTERVALO': 'bg-orange-100 text-orange-700 border-orange-200',
                                'RETORNO_INTERVALO': 'bg-blue-100 text-blue-700 border-blue-200',
                                'SAIDA': 'bg-red-100 text-red-700 border-red-200'
                              };
                              
                              const hora = new Date(registro.data_hora).toLocaleTimeString('pt-BR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              });
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="bg-white rounded-lg p-3 border border-gray-200 flex items-center justify-between"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`px-3 py-1 rounded-full text-xs font-medium border ${tipoColors[registro.tipo_marcacao] || 'bg-gray-100 text-gray-700'}`}>
                                      {tipoLabels[registro.tipo_marcacao] || registro.tipo_marcacao}
                                    </div>
                                    <div className="text-lg font-bold text-gray-900">{hora}</div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-2">
                                    {registro.status === 'AJUSTADA' && (
                                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                        Ajustado
                                      </span>
                                    )}
                                    {registro.origem === 'APP' && (
                                      <span className="text-xs text-gray-500">📱</span>
                                    )}
                                    {registro.origem === 'WEB' && (
                                      <span className="text-xs text-gray-500">💻</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={() => setShowHistorico(false)}
                className="w-full bg-blue-600 text-white rounded-xl py-3 font-semibold hover:bg-blue-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ajustes Solicitados */}
      {showAjustesSolicitados && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">Ajustes Solicitados</h2>
                <button
                  onClick={() => setShowAjustesSolicitados(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-indigo-100 text-sm">Acompanhe o status das suas solicitações</p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {carregandoAjustes ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                  <p className="text-gray-600">Carregando solicitações...</p>
                </div>
              ) : ajustesSolicitados.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="h-16 w-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-gray-600 text-lg mb-2">Nenhuma solicitação encontrada</p>
                  <p className="text-gray-500 text-sm">Suas solicitações de ajuste aparecerão aqui</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ajustesSolicitados.map((ajuste) => {
                    const statusColors = {
                      'pendente': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                      'aprovado': 'bg-green-100 border-green-300 text-green-800',
                      'reprovado': 'bg-red-100 border-red-300 text-red-800'
                    };

                    const statusIcons = {
                      'pendente': '⏳',
                      'aprovado': '✅',
                      'reprovado': '❌'
                    };

                    const statusLabels = {
                      'pendente': 'Pendente',
                      'aprovado': 'Aprovado',
                      'reprovado': 'Reprovado'
                    };

                    const tipoLabels = {
                      'entrada': 'Entrada',
                      'saida_intervalo': 'Saída Intervalo',
                      'retorno_intervalo': 'Retorno Intervalo',
                      'saida': 'Saída'
                    };

                    return (
                      <div 
                        key={ajuste.id} 
                        className={`rounded-xl border-2 p-4 ${statusColors[ajuste.status] || 'bg-gray-100 border-gray-300'}`}
                      >
                        {/* Header do Card */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{statusIcons[ajuste.status] || '📋'}</span>
                            <div>
                              <p className="font-bold text-lg">
                                {tipoLabels[ajuste.tipo_ponto?.toLowerCase()] || ajuste.tipo_ponto || '-'}
                              </p>
                              <p className="text-sm opacity-75">
                                {ajuste.data.split('-').reverse().join('/')} às {ajuste.hora}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                        {ajuste.status === 'pendente' && (
                          <button
                            onClick={() => handleEditarAjuste(ajuste)}
                            className="p-2 bg-white/70 rounded-full hover:bg-white transition-colors"
                            title="Editar solicitação"
                          >
                            <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <span className="px-3 py-1 rounded-full font-semibold text-sm">
                          {statusLabels[ajuste.status] || ajuste.status}
                        </span>
                      </div>
                        </div>

                        {/* Justificativa */}
                        <div className="bg-white/50 rounded-lg p-3 mb-3">
                          <p className="text-xs font-semibold mb-1 opacity-75">Justificativa:</p>
                          <p className="text-sm">{ajuste.justificativa}</p>
                        </div>

                        {/* Observações do RH (se houver) */}
                        {ajuste.observacoes_aprovacao && (
                          <div className="bg-white/70 rounded-lg p-3 mb-3 border border-current/20">
                            <p className="text-xs font-semibold mb-1 opacity-75">Observação do RH:</p>
                            <p className="text-sm">{ajuste.observacoes_aprovacao}</p>
                          </div>
                        )}

                        {/* Footer com datas */}
                        <div className="text-xs opacity-75 space-y-1">
                          <p>📅 Solicitado em: {new Date(ajuste.criado_em).toLocaleString('pt-BR')}</p>
                          {ajuste.data_aprovacao && (
                            <p>
                              {ajuste.status === 'aprovado' ? '✅' : '❌'} Processado em: {new Date(ajuste.data_aprovacao).toLocaleString('pt-BR')}
                            </p>
                          )}
                          {ajuste.aprovado_por && (
                            <p>👤 Por: {ajuste.aprovado_por}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={() => setShowAjustesSolicitados(false)}
                className="w-full bg-indigo-600 text-white rounded-xl py-3 font-semibold hover:bg-indigo-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Enviar Documento */}
      {showEnviarDocumento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">Enviar Documento</h2>
                <button
                  onClick={() => setShowEnviarDocumento(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-orange-100 text-sm">Envie atestados, comprovantes e outros documentos</p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Tipo de Documento */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    value={documentoForm.tipo_documento_id}
                    onChange={(e) => setDocumentoForm({...documentoForm, tipo_documento_id: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:outline-none text-base"
                  >
                    <option value="">Selecione o tipo</option>
                    {tiposDocumentos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Motivo */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Motivo / Observação *
                  </label>
                  <textarea
                    value={documentoForm.motivo}
                    onChange={(e) => setDocumentoForm({...documentoForm, motivo: e.target.value})}
                    placeholder="Explique brevemente o motivo do envio deste documento..."
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:outline-none text-base resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {documentoForm.motivo.length} caracteres
                  </p>
                </div>

                {/* Upload de Arquivo */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Arquivo *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-orange-500 transition-colors">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setDocumentoForm({...documentoForm, arquivo: file});
                        }
                      }}
                      className="hidden"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {documentoForm.arquivo ? (
                        <div>
                          <svg className="h-12 w-12 mx-auto text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="font-semibold text-gray-900">{documentoForm.arquivo.name}</p>
                          <p className="text-sm text-gray-500">{(documentoForm.arquivo.size / 1024).toFixed(2)} KB</p>
                          <p className="text-xs text-orange-600 mt-2">Clique para alterar</p>
                        </div>
                      ) : (
                        <div>
                          <svg className="h-12 w-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-gray-600 mb-1">Clique para selecionar o arquivo</p>
                          <p className="text-xs text-gray-500">PDF, JPG, PNG, DOC ou DOCX</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-2">
              <button
                onClick={handleEnviarDocumento}
                disabled={enviandoDocumento}
                className="w-full bg-orange-600 text-white rounded-xl py-3 font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviandoDocumento ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  'Enviar Documento'
                )}
              </button>
              <button
                onClick={() => setShowEnviarDocumento(false)}
                disabled={enviandoDocumento}
                className="w-full bg-gray-200 text-gray-700 rounded-xl py-3 font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Documentos Enviados */}
      {showDocumentosEnviados && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">Meus Documentos</h2>
                <button
                  onClick={() => setShowDocumentosEnviados(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-teal-100 text-sm">Histórico de documentos enviados ao RH</p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {carregandoMeusDocumentos ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Carregando documentos...</p>
                </div>
              ) : meusDocumentos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="h-16 w-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-600 text-lg mb-2">Nenhum documento enviado</p>
                  <p className="text-gray-500 text-sm">Seus documentos aparecerão aqui após o envio</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {meusDocumentos.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="border-2 border-gray-200 rounded-2xl p-5 hover:shadow-lg transition-shadow"
                    >
                      {/* Header do Card */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">
                            {doc.tipo_documento_nome}
                          </h3>
                          <p className="text-sm text-gray-600">
                            📄 {doc.arquivo_nome}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          doc.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' :
                          doc.status === 'visualizado' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {doc.status === 'pendente' ? '⏳ Pendente' :
                           doc.status === 'visualizado' ? '✅ Visualizado' :
                           doc.status}
                        </span>
                      </div>

                      {/* Motivo */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Motivo:</p>
                        <p className="text-sm text-gray-900">{doc.motivo}</p>
                      </div>

                      {/* Observações do RH (se houver) */}
                      {doc.observacoes_rh && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <p className="text-xs font-semibold text-blue-700 mb-1">💬 Observações do RH:</p>
                          <p className="text-sm text-blue-900">{doc.observacoes_rh}</p>
                        </div>
                      )}

                      {/* Informações */}
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>📅 Enviado em: {new Date(doc.criado_em).toLocaleString('pt-BR')}</p>
                        {doc.visualizado_em && (
                          <p>👁️ Visualizado em: {new Date(doc.visualizado_em).toLocaleString('pt-BR')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={() => setShowDocumentosEnviados(false)}
                className="w-full bg-teal-600 text-white rounded-xl py-3 font-semibold hover:bg-teal-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Solicitar Férias */}
      {showSolicitarFerias && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">Solicitar Férias</h2>
                <button
                  onClick={() => setShowSolicitarFerias(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-purple-100 text-sm">Gerencie suas férias</p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Períodos Disponíveis */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3">Períodos Disponíveis</h3>
                {meusPeriodosFerias.filter(p => p.status === 'active' && p.days_remaining > 0).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <p className="text-gray-600">Nenhum período disponível</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {meusPeriodosFerias.filter(p => p.status === 'active' && p.days_remaining > 0).map((periodo) => (
                      <div key={periodo.id} className="border-2 border-green-200 bg-green-50 rounded-xl p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="text-sm text-gray-600">Período Aquisitivo</p>
                            <p className="font-semibold">
                              {new Date(periodo.acquisition_start).toLocaleDateString('pt-BR')} - {new Date(periodo.acquisition_end).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                            {periodo.days_remaining} dias
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setPeriodoFeriasSelecionado(periodo);
                            setShowNovaFeriasModal(true);
                          }}
                          className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                        >
                          + Nova Solicitação
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Minhas Solicitações */}
              <div>
                <h3 className="font-bold text-lg mb-3">Minhas Solicitações</h3>
                {minhasSolicitacoesFerias.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <p className="text-gray-600 text-sm">Nenhuma solicitação ainda</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {minhasSolicitacoesFerias.map((sol) => (
                      <div key={sol.id} className={`border-2 rounded-xl p-4 ${
                        sol.status === 'pending' ? 'border-yellow-200 bg-yellow-50' :
                        sol.status === 'approved' ? 'border-green-200 bg-green-50' :
                        'border-red-200 bg-red-50'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">
                              {new Date(sol.start_date).toLocaleDateString('pt-BR')} - {new Date(sol.end_date).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="text-sm text-gray-600">{sol.days_requested} dias</p>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                            sol.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                            sol.status === 'approved' ? 'bg-green-200 text-green-800' :
                            'bg-red-200 text-red-800'
                          }`}>
                            {sol.status === 'pending' ? 'Pendente' :
                             sol.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                          </span>
                        </div>
                        {sol.notes && (
                          <p className="text-sm text-gray-700 mt-2">💬 {sol.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={() => setShowSolicitarFerias(false)}
                className="w-full bg-purple-600 text-white rounded-xl py-3 font-semibold hover:bg-purple-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nova Solicitação de Férias */}
      {showNovaFeriasModal && periodoFeriasSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold">Nova Solicitação</h2>
                <button
                  onClick={() => setShowNovaFeriasModal(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-green-100 text-sm">
                Disponível: {periodoFeriasSelecionado.days_remaining} dias
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data Início</label>
                <input
                  type="date"
                  value={formFerias.start_date}
                  onChange={(e) => setFormFerias({...formFerias, start_date: e.target.value})}
                  className="w-full p-3 border rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Data Fim</label>
                <input
                  type="date"
                  value={formFerias.end_date}
                  onChange={(e) => setFormFerias({...formFerias, end_date: e.target.value})}
                  min={formFerias.start_date}
                  className="w-full p-3 border rounded-xl"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-blue-800">
                  Dias calculados: {formFerias.days_of_enjoyment} dias
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Observações (Opcional)</label>
                <textarea
                  value={formFerias.notes}
                  onChange={(e) => setFormFerias({...formFerias, notes: e.target.value})}
                  className="w-full p-3 border rounded-xl"
                  rows="3"
                  placeholder="Informações adicionais..."
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formFerias.request_13th_advance}
                    onChange={(e) => setFormFerias({...formFerias, request_13th_advance: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="text-sm">Solicitar adiantamento do 13º</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 space-y-2 bg-gray-50">
              <button
                onClick={enviarSolicitacaoFerias}
                className="w-full bg-green-600 text-white rounded-xl py-3 font-semibold hover:bg-green-700 transition-colors"
              >
                Enviar Solicitação
              </button>
              <button
                onClick={() => setShowNovaFeriasModal(false)}
                className="w-full bg-gray-200 text-gray-700 rounded-xl py-3 font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal: Alteração de Senha Obrigatória */}
      <Dialog open={showPasswordChangeModal} onOpenChange={setShowPasswordChangeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Alteração de Senha Obrigatória
            </DialogTitle>
            <DialogDescription>
              <div className="flex items-start gap-2 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  Para registrar o ponto, você precisa alterar sua senha. 
                  Após alterar, faça login novamente e tente registrar o ponto.
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowPasswordChangeModal(false)}
            >
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Alterar Senha
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NOVO: Modal do Mapa - Visualizar localização GPS */}
      {showMapModal && location && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Map className="h-6 w-6" />
                <h2 className="text-xl font-bold">Sua Localização GPS</h2>
              </div>
              <button
                onClick={() => setShowMapModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-4">
              {/* Informações */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Latitude:</span>
                    <div className="font-mono text-blue-600">{location.latitude?.toFixed(6)}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Longitude:</span>
                    <div className="font-mono text-blue-600">{location.longitude?.toFixed(6)}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Precisão:</span>
                    <div className={`font-semibold ${
                      location.accuracy < 20 ? 'text-green-600' :
                      location.accuracy < 50 ? 'text-yellow-600' :
                      'text-orange-600'
                    }`}>
                      ±{location.accuracy?.toFixed(0)} metros
                    </div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Leituras:</span>
                    <div className="text-blue-600">
                      {location.fromMultipleReadings ? `${location.numReadings} leituras` : '1 leitura'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mapa */}
              <div className="relative w-full h-[400px] bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
                <iframe
                  src={`https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=18&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Localização"
                ></iframe>
              </div>

              {/* Alertas de Precisão */}
              {location.accuracy > 50 && (
                <div className="mt-4 bg-orange-50 border border-orange-300 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-semibold text-orange-800">Precisão Baixa</p>
                      <p className="text-sm text-orange-700">
                        A precisão do GPS está em {location.accuracy?.toFixed(0)}m. 
                        Para melhorar: saia de ambientes fechados, aguarde alguns segundos
                        ou use o botão "Recalibrar GPS".
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {location.accuracy <= 20 && (
                <div className="mt-4 bg-green-50 border border-green-300 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-2xl">✅</span>
                    <div>
                      <p className="font-semibold text-green-800">Excelente Precisão!</p>
                      <p className="text-sm text-green-700">
                        GPS com precisão de {location.accuracy?.toFixed(0)}m. Localização confiável.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Link para Google Maps */}
              <div className="mt-4">
                <a
                  href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-blue-600 text-white rounded-lg py-3 font-semibold hover:bg-blue-700 transition-colors"
                >
                  Abrir no Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default PontoPWAPage;
